/**
 * CMJRoomWnd 相当 — ルーム待機画面 (AP-09 §1-12)
 * レガシー: legacy/client/HgMajak2/MJRoomWnd1.cpp / MJRoomWnd.h
 *
 * ウィンドウ全体: 1019×704px
 * ゲーム卓エリア:  m_wndGame.Create(5, 31, this) → mj_board.png (789×704) left=5, top=31
 * サイドバー:      X_SIDEBAR=794, Y_SIDEBAR=31, W_SIDEBAR=225, H_SIDEBAR=704
 *                  背景: mj_sideBg.png (225×704)
 *
 * 定数 (MJRoomWnd.h より):
 *   HG_GAME_CHATLIST_LEFT=808  TOP=345  RIGHT=1016  BOTTOM=603
 *   HG_GAME_CHATEDIT_LEFT=809  TOP=624  RIGHT=1010  BOTTOM=640
 *   MAJAK_ROOMTITLE_LEFT=815   TOP=49   RIGHT=941   BOTTOM=63
 *   HG_GAME_STATUSWND_LEFT=808 TOP=81   RIGHT=1016  BOTTOM=211
 *   HG_GAME_NOTICE_LEFT=809    TOP=325  RIGHT=1009  BOTTOM=337
 *   X_VIEWER_WINDOW=805        Y=235    RIGHT=1010  BOTTOM=310
 */
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, type CSSProperties } from 'react'
import * as SignalR from '../../api/signalr'
import { useAuthStore } from '../../store/authStore'
import { useGamePlayerStore } from '../../store/gamePlayerStore'
import { useCustomSkinStore } from '../../store/customSkinStore'
import { isOk, showError, showMessage } from '../../utils/msgbox'
import { readNoticePayload, type NoticeDisplay } from '../../utils/notice'
import { sendAccuseComplaint } from '../../utils/accuse'
import { getTabSessionId } from '../../utils/tabSession'
import { MAJAK_ACCUSE_EVENT } from '../../components/MajakFrame'
import GameReconnectLoading from '../../components/GameReconnectLoading'
import CfgDlg, { loadMajakConfig, saveMajakConfig, type MJConfig } from './dialogs/CfgDlg'
import AccuseDlg from './dialogs/AccuseDlg'
import LevelupDlg from './dialogs/LevelupDlg'
import PlayerInfoWnd, { type PlayerInfo as PlayerInfoDialogData } from './dialogs/PlayerInfoWnd'
import ViewerListWnd, { type ViewerEntry } from '../ingame/ViewerListWnd'
import SlideAnnounce, { type SlideAnnounceData } from '../ingame/SlideAnnounce'
import HanRes, { type HanResPlayer } from '../ingame/HanRes'
import { FORCED_HAN_RESULT, FORCE_HAN_RESULT_FOR_TEST } from '../ingame/forcedHanResult'
import KyoRes, { type KyoResData } from '../ingame/KyoRes'
import { FORCED_KYO_RESULT, FORCE_KYO_RESULT_FOR_TEST } from '../ingame/forcedKyoResult'
import MiniChannelWnd from './MiniChannelWnd'
import { getAvatarUrl, getDefaultAvatarUrl, getShortAvatarUrl, handleShortAvatarError } from '../../utils/resources'
import { configureMajakSound, playMajakChat, playMajakSid, SID_EXIT, SID_JOIN } from '../../utils/majakSound'
import { createGame, destroyGame, freezeGameFrame, GAME_HEIGHT, GAME_WIDTH, suspendGame } from '../../game/GameInstance'
import { shouldRequestInitialGameResync } from '../../game/resyncState'
import {
  GAME_AUTO_CONTROL_EVENT,
  GAME_KYOKU_STARTED_EVENT,
  GAME_LOCAL_REACH_EVENT,
  enableAutoTapAfterReach,
  resetAutoControlForNewKyoku,
  shouldEnableAutoPassAtKyokuStart,
  type AutoControlState,
} from '../../game/autoControl'
import { GAME_BOARD_SURROUND_COLOR_EVENT, GAME_LOAD_PROGRESS_EVENT, GAME_LOAD_STEPS, type GameLoadStep } from '../../game/gameLoadProgress'
import { finalizePaifuRecording } from '../../game/paifuRecording'
import { getLegacyKyoResultDelayMs } from '../../game/legacyAnimations'
import { applyTengokuTextColor, getLegacyBoardImageUrl, getLegacyBoardSoundSkinId, getLegacyFullUiSkinId, getLegacyRoomPalette, isTengokuBoardSkin } from '../../utils/legacySkinPalette'
import { useOutgameLayoutMode } from '../../hooks/useOutgameLayoutMode'

const IMG = '/assets/images/game'
const CMD_USE_EMOTICON = 'mjkc24e'
const CMD_YAKUMAN_BONUS = 'mjkc23e'
const CMD_GAME_PLAY = 'playing'
const ACT_PAS = 1
const KEY_EMOTICON_ID = 'mjkk63e'
const KEY_EMOTICON_AVATAR_ID = 'mjkk64e'
const KEY_YAKU_NAME = 'mjkk62e'
const EMOTICON_COUNT = 6
const EMOTICON_FRAME_MS = 33
const EMOTICON_LEGACY_IDS = [1, 3, 5, 7, 8, 11]
const ABANDON_ROOM_STORAGE_KEY = 'majak:abandonRoomOnNextLobbyEnter'
const GAME_STATUS_EVENT = 'majak:game-status'
const GAME_EFFECT_WINDOW_EVENT = 'majak:game-effect-window'
const GAME_EFFECT_SETTLE_MS = 80
const GAME_FOCUS_CHAT_EVENT = 'majak:game-focus-chat'
const GAME_SYNC_EVENT = 'majak:game-sync'
const KYO_RESULT_ACTION_EVENT = 'majak:kyo-result-action'
const KYO_RESULT_PROGRESS_EVENT = 'majak:kyo-result-progress'
const KYO_RESULT_CONFIRMED_EVENT = 'majak:kyo-result-confirmed'
const PAIFU_ROTATE_EVENT = 'majak:paifu-rotate'
const PAIFU_HAND_OPEN_EVENT = 'majak:paifu-hand-open'
const DEBUG_GAME = import.meta.env.VITE_DEBUG_GAME === '1'

const ROOM_ENTER_ERROR_MESSAGES: Record<number, string> = {
  10005: 'パスワードが違います。',
  10009: 'このルームには入室できません。',
  10011: 'ルームサーバーが混雑しています。しばらくしてからもう一度お試しください。',
  10019: 'ルームへの入室要求が不正です。',
  10021: '既に別のルームに入室しています。',
  10022: '既にこのルームに入室しています。',
  10027: '接続情報が一致しません。チャンネルから入り直してください。',
  30001: 'ルームへの入室に失敗しました。',
  30011: 'このルームの入室条件を満たしていません。',
}

function roomEnterErrorMessage(data: Record<string, unknown>): string {
  const rawMessage = data.k2e ?? data.message
  const message = typeof rawMessage === 'string' ? rawMessage.trim() : ''
  if (message && message !== 'エラー') return message

  const failCode = Number(data.failcode ?? data.failCode)
  return ROOM_ENTER_ERROR_MESSAGES[failCode] ?? 'ルームへの入室に失敗しました。'
}

function getDocumentNavigationType() {
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  return entry?.type ?? ''
}
const MAX_ROOM_LOG_MESSAGES = 200

type ActiveEmoticon = {
  id: string
  pix: string
  type: number
  pos: number
  startedAt: number

}

type EmoticonStep = {
  frame: number
  alpha: number
}

function appendRoomLog(previous: ChatMsg[], ...messages: ChatMsg[]): ChatMsg[] {
  const next = [...previous, ...messages]
  return next.length > MAX_ROOM_LOG_MESSAGES ? next.slice(-MAX_ROOM_LOG_MESSAGES) : next
}

function buildGetMemberListPayload(channelId: string) {
  const subId = extractSubId(channelId)
  return {
    gameId: 'MAJAK4',
    k22e: 'MAJAK4',
    subId,
    k23e: subId,
    channelId,
    k24e: channelId,
  }
}

function buildEnterChannelPayload(
  channelId: string,
  player: ReturnType<typeof useAuthStore.getState>['player'],
  isContinue = false,
) {
  const subId = extractSubId(channelId)
  const pix = player?.pix ?? ''
  const avatarId = player?.avatarId ?? ''
  const nickname = player?.name ?? ''
  return {
    gameId: 'MAJAK4',
    k22e: 'MAJAK4',
    subId,
    k23e: subId,
    channelId,
    k24e: channelId,
    pix,
    k3e: pix,
    nickname,
    name: nickname,
    k8e: nickname,
    avatarId,
    k7e: avatarId,
    password: player?.password ?? '',
    tabId: getTabSessionId(),
    mjkk33e: isContinue,
  }
}

function buildExitRoomPayload(player: ReturnType<typeof useAuthStore.getState>['player'], seatPos: number | undefined, isViewer = false) {
  const pix = player?.pix ?? ''
  const name = player?.name ?? ''
  const playerType = isViewer ? 'v5e' : 'v4e'
  const pos = isViewer ? -1 : seatPos ?? -1
  return {
    playerType,
    k57e: playerType,
    playerPos: pos,
    k58e: pos,
    pix,
    k3e: pix,
    name,
    k8e: name,
  }
}

type CreateRoomPayloadState = {
  roomId?: number
  roomOption?: string
  maxViewer?: number
  moneyRate?: number
  minMoney?: number
  maxMoney?: number
  isPrivate?: boolean
  roomTitle?: string
  roomPassword?: string
  trainingAiLevel?: 'Legacy' | 'Advanced'
}

function buildCreateRoomPayload(
  channelId: string,
  player: ReturnType<typeof useAuthStore.getState>['player'],
  state: CreateRoomPayloadState,
) {
  const subId = channelId.length >= 11 ? channelId.substring(6, 11) : channelId
  const pix = player?.pix ?? ''
  const roomPassword = String(state.roomPassword ?? '')
  const isPrivate = Boolean(state.isPrivate)
  const maxViewer = Number(state.maxViewer ?? 12)
  const roomTitle = String(state.roomTitle ?? '')
  const roomOption = String(state.roomOption ?? '')
  const roomId = Number(state.roomId ?? 0)
  return {
    pix,
    k3e: pix,
    roomId,
    k42e: roomId,
    roomPwd: roomPassword,
    k67e: roomPassword,
    roomTitle,
    k45e: roomTitle,
    roomOption,
    k46e: roomOption,
    roomMinCnt: 0,
    k127e: 0,
    roomLimitCnt: 4,
    k66e: 4,
    roomType: isPrivate ? 'private' : 'normal',
    isPrivate,
    k68e: isPrivate ? 'Y' : 'N',
    maxViewer,
    k69e: maxViewer,
    gameId: 'MAJAK4',
    k22e: 'MAJAK4',
    subId,
    k23e: subId,
    channelId,
    k24e: channelId,
    moneyRate: Number(state.moneyRate ?? 500),
    minMoney: Number(state.minMoney ?? 0),
    maxMoney: Number(state.maxMoney ?? 0),
    trainingAiLevel: state.trainingAiLevel ?? 'Advanced',
  }
}

const BOARD_X = 5
const BOARD_Y = 0
const ROOM_W = 1019
const ROOM_H = 704
const MOBILE_INGAME_FOCUS_W = 794
const MOBILE_INGAME_VISIBLE_H = 600
const MOBILE_INGAME_OFFSET_Y = -180
const MOBILE_HAN_RES_W = 520
const MOBILE_HAN_RES_H = 580
const MOBILE_HAN_RES_OFFSET_Y = -18
const MOBILE_TOP_RIGHT_HUD_PANEL_BOTTOM = 122
const MOBILE_INGAME_TOOL_HUD_GAP = -18

const OPTION_ICON = {
  set: `${IMG}/mj_opt_0.png`,
  kui: `${IMG}/mj_opt_3.png`,
  uma: `${IMG}/mj_opt_1.png`,
  ron: `${IMG}/mj_optron.png`,
  red: `${IMG}/mj_opt_5.png`,
  spd: `${IMG}/mj_opt_4.png`,
  opn: `${IMG}/mj_opt_6.png`,
  cht: `${IMG}/mj_opt_7.png`,
  ach: `${IMG}/mj_opt_8.png`,
} as const

const MEM_POS = [
  { avt: { x: 2, y: 582 }, gls: { x: 92, y: 569 }, text: { x: 49, y: 625 }, name: { x: 2, y: 687 }, ttl: { x: 49, y: 583 }, trk: { x: 1, y: 581 } },
  { avt: { x: 742, y: 582 }, gls: { x: 678, y: 569 }, text: { x: 689, y: 625 }, name: { x: 689, y: 687 }, ttl: { x: 690, y: 583 }, trk: { x: 688, y: 581 } },
  { avt: { x: 742, y: 2 }, gls: { x: 678, y: 113 }, text: { x: 689, y: 45 }, name: { x: 689, y: 107 }, ttl: { x: 690, y: 3 }, trk: { x: 688, y: 1 } },
  { avt: { x: 2, y: 2 }, gls: { x: 92, y: 113 }, text: { x: 49, y: 45 }, name: { x: 2, y: 107 }, ttl: { x: 49, y: 3 }, trk: { x: 1, y: 1 } },
] as const

const KEY_COUNT = 'k25e'
const KEY_TITLE_TYPE = 'mjkk48e'
const KEY_TITLE_CODE = 'mjkk49e'
const KEY_TITLE_NAME = 'mjkk50e'
const KEY_GEM_GAME = 'mjkk56e'
const LEGACY_PROXY_GUIDE = [
  '接続が切れた人は以後すべてツモ切りとなります。',
  '再接続すると対局中に復帰できます。',
  '成績は対局終了後の成績がそのまま反映されます。',
]
const LEGACY_TOURNAMENT_LINEOFF_GUIDE = [
  '接続が切れた人は以後すべてツモ切りとなります。',
  'トーナメント戦の落ち戻りはトーナメントロビーへお越しください。',
]
const LEGACY_CANCEL_GUIDE_DANI = '段位戦ではチャットは利用できません。'
const LEGACY_CANCEL_GUIDE_STOP = 'ポン・チー・ミンカン・ロンができるときはゲー ムの進行がしばらく停止します。'
const LEGACY_CANCEL_GUIDE_PASS = '見送りはマウスの右クリック、「パス」ボタン、[Enter]キーです。'
const LEGACY_GEM_GAME_STATUS = [
  '',
  'この対戦は龍珠獲得戦になります。',
  'この対戦は龍珠獲得戦BIGになります。',
]

const LEGACY_STATUS_GUIDE: ChatMsg[] = []

function makeEmoticonSteps(frames: number[], repeat: number, fadeFrame: number): EmoticonStep[] {
  const base = Array.from({ length: repeat }, () => frames).flat().map(frame => ({ frame, alpha: 255 }))
  return [...base, { frame: fadeFrame, alpha: 191 }, { frame: fadeFrame, alpha: 127 }, { frame: fadeFrame, alpha: 63 }, { frame: fadeFrame, alpha: 0 }]
}

const EMOTICON_STEPS: EmoticonStep[][] = [
  makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5], 7, 5),
  [...makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6], 1, 6).slice(0, 14), ...Array.from({ length: 68 }, () => ({ frame: 6, alpha: 255 })), { frame: 6, alpha: 191 }, { frame: 6, alpha: 127 }, { frame: 6, alpha: 63 }, { frame: 6, alpha: 0 }],
  makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 3, 3, 2, 2, 1, 1, 0, 0], 5, 1),
  makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 3, 3, 2, 2, 1, 1, 0, 0], 5, 1),
  [...makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7], 1, 7).slice(0, 16), ...Array.from({ length: 66 }, () => ({ frame: 7, alpha: 255 })), { frame: 7, alpha: 191 }, { frame: 7, alpha: 127 }, { frame: 7, alpha: 63 }, { frame: 7, alpha: 0 }],
  makeEmoticonSteps([0, 0, 1, 1, 2, 2, 3, 3], 10, 2),
]

function normalizeEmoticonType(value: unknown): number | null {
  const raw = Number(value)
  if (!Number.isFinite(raw)) return null
  if (raw >= 0 && raw < EMOTICON_COUNT) return raw
  const legacyIndex = EMOTICON_LEGACY_IDS.indexOf(raw)
  return legacyIndex >= 0 ? legacyIndex : null
}

function EmoticonAnimation({ item, now }: { item: ActiveEmoticon; now: number }) {
  const steps = EMOTICON_STEPS[item.type] ?? EMOTICON_STEPS[0]
  const frameIndex = Math.max(0, Math.min(steps.length - 1, Math.floor((now - item.startedAt) / EMOTICON_FRAME_MS)))
  const step = steps[frameIndex]
  const pos = MEM_POS[item.pos] ?? MEM_POS[0]
  const avatarPos = roomPoint(pos.avt)
  const dir = item.pos === 1 || item.pos === 2 ? 'l' : 'r'
  return (
    <div
      style={{
        position: 'absolute',
        left: Math.max(0, Math.min(ROOM_W - 96, avatarPos.left + (dir === 'r' ? 36 : -86))),
        top: Math.max(0, Math.min(ROOM_H - 96, avatarPos.top - 8)),
        width: 96,
        height: 96,
        opacity: step.alpha / 255,
        backgroundImage: `url(${IMG}/emo_${dir}_${String(item.type).padStart(2, '0')}.png)`,
        backgroundPosition: `${-96 * step.frame}px 0`,
        backgroundRepeat: 'no-repeat',
        imageRendering: 'pixelated',
        pointerEvents: 'none',
      }}
    />
  )
}

function readRoomPlayer(data: Record<string, unknown>): PlayerInfoType | null {
  const playerId = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? data.playerId ?? '')
  if (!playerId) return null
  const pos = Number(data.k58e ?? data.playerPos ?? data.seatPos ?? data.pos ?? -1)
  if (pos < 0 || pos > 3) return null
  const readyValue = data.ready ?? data.isReady ?? data.okButton ?? data.ok
  return {
    playerId,
    name: String(data.mjkk34e ?? data.k8e ?? data.nickName ?? data.nickname ?? data.name ?? playerId),
    rating: Number(data.k31e ?? data.rating ?? 0),
    pos: pos as 0 | 1 | 2 | 3,
    ready: readyValue === true || readyValue === 1 || readyValue === '1' || readyValue === 'true',
    isHost: Boolean(data.isHost ?? false),
    avatarId: data.k7e != null || data.avatarId != null ? String(data.k7e ?? data.avatarId) : undefined,
    sex: data.k11e != null || data.sex != null ? String(data.k11e ?? data.sex) : undefined,
    slevel: String(data.k32e ?? data.slevel ?? data.dan ?? ''),
    nlevel: Number(data.k33e ?? data.nlevel ?? 0),
    isProxy: Boolean(data.isProxy ?? data.proxy ?? false),
    skillCnt: Number(data.skillCnt ?? data.skillCount ?? 0),
    majakTitle: readTitleCode(data.mjkk47e ?? data.majakTitle),
    trickTitle: readTitleCode(data.mjkk46e ?? data.trickTitle),
  }
}

function getMajakTitleSrc(code?: number) {
  if (!code) return ''
  const prefix = code < 1000 ? 'mj_title' : 'mj_ctitle'
  const value = code < 1000 ? code : code - 1000
  return `${IMG}/${prefix}_${String(value).padStart(3, '0')}.png`
}

function getTrickTitleSrc(code?: number) {
  if (!code) return ''
  return `${IMG}/mj_skill_${String(code).padStart(3, '0')}.png`
}

function readOptionDigit(option: string | undefined, index: number, fallback: number) {
  const char = option?.charAt(index) ?? ''
  return /^\d$/.test(char) ? Number(char) : fallback
}

function extractSubId(channelId?: string) {
  const id = channelId ?? ''
  return id.length >= 11 ? id.substring(6, 11) : id
}

function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function asLegacyBool(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'v1e'
}

function gameEndStatusLines(data: Record<string, unknown>): string[] {
  const rawEnd = String(data.gameEnd ?? '').toLowerCase()
  const endValue = asNumber(data.gameEndValue, -1)
  let endText: string | null = null
  if (rawEnd === 'set' || endValue === 1) {
    endText = Boolean(data.isHanchanRule) ? '----半荘終了----' : '----東風戦終了----'
  } else if (rawEnd === 'stop' || endValue === 2) {
    endText = '----対局中断----'
  } else if (rawEnd === 'tobi' || endValue === 3) {
    endText = '----ハコテン終了----'
  } else if (rawEnd === 'hora' || endValue === 4) {
    endText = '----アガリ止め終了----'
  }
  return endText ? [endText, '----対局終了----'] : []
}

function readHanResPlayers(data: Record<string, unknown>, myPix: string): HanResPlayer[] {
  const users = Array.isArray(data.users)
    ? data.users as Array<Record<string, unknown>>
    : []
  return users.map((user, index) => ({
    pix: String(user.pix ?? user.k3e ?? user['member' + 'Id'] ?? ''),
    name: String(user.name ?? ''),
    avatarId: String(user.avatarId ?? ''),
    sex: String(user.sex ?? ''),
    charaId: asNumber(user.charaId ?? user.customCostume, 0),
    seatPos: (asNumber(user.seatPos, index) % 4) as 0 | 1 | 2 | 3,
    rank: (asNumber(user.rank ?? (asNumber(user.ranking, index + 1) - 1), index) % 4) as 0 | 1 | 2 | 3,
    point: asNumber(user.point, 0),
    setBal: asNumber(user.setBal, 0),
    setTen: asNumber(user.setTen, 0),
    setUma: asNumber(user.setUma, 0),
    setTor: user.setTor !== undefined ? asNumber(user.setTor, 0) : undefined,
    setTip: user.setTip !== undefined ? asNumber(user.setTip, 0) : undefined,
    coinGain: asNumber(user.coinGain, 0),
    coinNeed: asNumber(user.coinNeed, 0),
    prevNlevel: user.prevNlevel !== undefined ? asNumber(user.prevNlevel, 0) : undefined,
    nlevel: user.nlevel !== undefined ? asNumber(user.nlevel, 0) : undefined,
    levelName: String(user.slevel ?? ''),
    rating: user.rating !== undefined ? asNumber(user.rating, 0) : undefined,
    ratingChange: user.ratingChange !== undefined ? asNumber(user.ratingChange, 0) : undefined,
    matchCnt: user.matchCnt !== undefined ? asNumber(user.matchCnt, 0) : undefined,
    winCnt: user.winCnt !== undefined ? asNumber(user.winCnt, 0) : undefined,
    defeatCnt: user.defeatCnt !== undefined ? asNumber(user.defeatCnt, 0) : undefined,
    drawCnt: user.drawCnt !== undefined ? asNumber(user.drawCnt, 0) : undefined,
    gameMoney: user.gammoney !== undefined ? asNumber(user.gammoney, 0) : undefined,
    moneyChange: user.moneyChange !== undefined ? asNumber(user.moneyChange, 0) : undefined,
    dealerFee: user.dealerFee !== undefined ? asNumber(user.dealerFee, 0) : undefined,
    gemCount: user.gemCount !== undefined ? asNumber(user.gemCount, 0) : undefined,
    experience: user.experience !== undefined ? asNumber(user.experience, 0) : undefined,
    expGain: user.expGain !== undefined ? asNumber(user.expGain, 0) : undefined,
    horaCnt: user.horaCnt !== undefined ? asNumber(user.horaCnt, 0) : undefined,
    horaPoint: user.horaPoint !== undefined ? asNumber(user.horaPoint, 0) : undefined,
    hojuCnt: user.hojuCnt !== undefined ? asNumber(user.hojuCnt, 0) : undefined,
    richiCnt: user.richiCnt !== undefined ? asNumber(user.richiCnt, 0) : undefined,
    furoCnt: user.furoCnt !== undefined ? asNumber(user.furoCnt, 0) : undefined,
    doraCnt: user.doraCnt !== undefined ? asNumber(user.doraCnt, 0) : undefined,
    richiHoraCnt: user.richiHoraCnt !== undefined ? asNumber(user.richiHoraCnt, 0) : undefined,
    prevGradeLevel: user.prevGradeLevel !== undefined ? asNumber(user.prevGradeLevel, 0) : undefined,
    gradeLevel: user.gradeLevel !== undefined ? asNumber(user.gradeLevel, 0) : undefined,
    prevGradePoint: user.prevGradePoint !== undefined ? asNumber(user.prevGradePoint, 0) : undefined,
    gradePoint: user.gradePoint !== undefined ? asNumber(user.gradePoint, 0) : undefined,
    gradeAddPoint: user.gradeAddPoint !== undefined ? asNumber(user.gradeAddPoint, 0) : undefined,
    gradeNextPoint: user.gradeNextPoint !== undefined ? asNumber(user.gradeNextPoint, 0) : undefined,
    gradeUpDown: user.gradeUpDown !== undefined ? asNumber(user.gradeUpDown, 0) : undefined,
    isMe: String(user.pix ?? user.k3e ?? user['member' + 'Id'] ?? '') === myPix,
  }))
}

function gemGameStatusText(value: unknown): string | null {
  const gemGame = asNumber(value, 0)
  return LEGACY_GEM_GAME_STATUS[gemGame] ?? null
}

function readTitleCode(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const text = String(value ?? '')
  if (!text) return 0
  const direct = Number(text)
  if (Number.isFinite(direct)) return direct
  const match = text.match(/mjk([tsc])(\d{3})/i)
  if (!match) return 0
  const code = Number(match[2])
  return match[1].toLowerCase() === 'c' ? code + 1000 : code
}

function isDaniChannel(channelId?: string) {
  return extractSubId(channelId)[2] === 'G'
}

function isReplayChannel(channelId?: string) {
  return extractSubId(channelId)[2] === 'V'
}

function isTrainingChannel(channelId?: string) {
  return extractSubId(channelId)[2] === 'T'
}
function isAutoMatchingChannel(channelId?: string) {
  return extractSubId(channelId)[1] === 'Z'
}

function optionSprite(src: string, value: number, x: number, y: number, maxFrame = 8) {
  const frame = Math.max(0, Math.min(maxFrame, value))
  return (
    <div
      key={`${src}-${x}-${y}`}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: 17,
        height: 17,
        backgroundImage: `url(${src})`,
        backgroundPosition: `${-17 * frame}px 0`,
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  )
}

function roomPoint(pos: { x: number; y: number }) {
  return { left: BOARD_X + pos.x, top: BOARD_Y + pos.y }
}

function defaultSkinSrc(src: string): string {
  return src.replace(/^(.*)\/skin\/\d+\/([^/]+)_\d+\.png$/i, '$1/$2.png')
}

function seatToLegacyLoc(seatPos: number, viewSeatPos: number | undefined): 0 | 1 | 2 | 3 {
  const view = viewSeatPos != null && viewSeatPos >= 0 && viewSeatPos <= 3 ? viewSeatPos : 0
  return ((4 + seatPos - view) % 4) as 0 | 1 | 2 | 3
}

/** ====================================================================
 * CMJBmpButton 相当 — AP-06 §2 4フレームスプライトボタン
 * ==================================================================== */
function SpriteButton({
  src,
  fallbackSrc,
  frameW,
  frameH,
  x,
  y,
  onClick,
  title,
  hidden,
  disabled,
  checked,
}: {
  src: string
  fallbackSrc?: string
  frameW: number
  frameH: number
  x: number
  y: number
  onClick: () => void
  title?: string
  hidden?: boolean
  disabled?: boolean
  checked?: boolean
}) {
  const [frameIdx, setFrameIdx] = useState(0)
  if (hidden) return null
  const spriteFrame = disabled ? 1 : checked ? 3 : frameIdx
  const derivedFallbackSrc = fallbackSrc ?? src.replace(/^(.*)\/skin\/\d+\/([^/]+)_\d+\.png$/i, '$1/$2.png')
  const backgroundImage = derivedFallbackSrc !== src ? `url(${src}), url(${derivedFallbackSrc})` : `url(${src})`
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setFrameIdx(2)}
      onMouseLeave={() => !disabled && setFrameIdx(0)}
      onMouseDown={() => !disabled && setFrameIdx(3)}
      onMouseUp={() => !disabled && setFrameIdx(2)}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: frameW,
        height: frameH,
        display: 'block',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage,
        backgroundPosition: `${-spriteFrame * frameW}px 0`,
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: disabled ? 'default' : 'pointer',
        outline: 'none',
        opacity: 1,
        imageRendering: 'pixelated',
        pointerEvents: hidden ? 'none' : 'auto',
      }}
    />
  )
}

/** ====================================================================
 * プレイヤー情報 (CHgPlayerInfo 相当) — 将来 PlayerList 受信時に使用
 * ==================================================================== */
export type PlayerInfoType = {
  playerId: string
  name: string
  rating: number
  pos: 0 | 1 | 2 | 3  // 0-3: player seat
  ready: boolean
  isHost: boolean
  avatarId?: string
  sex?: string
  slevel?: string
  nlevel?: number
  isProxy?: boolean
  skillCnt?: number
  majakTitle?: number
  trickTitle?: number
}

type ChannelMemberEntry = {
  pix: string
  name: string
  rating: number
  gamMoney?: number
  avatarId?: string
  sex?: string
  slevel?: string
  location?: string
  roomId?: number
}

/** チャットメッセージ型
 *  レガシー MJRoomWnd1.cpp:830-865 (ProcessCommonChatService) より:
 *    送信者種別ごとに 1行全体を 1色 で描画する。
 *    self    : MAJAK_RGB_CHATSELF   = RGB(  0, 48, 96) = #003060
 *    player  : MAJAK_RGB_CHATOTHER  = RGB(  0,  0,  0) = #000000
 *    viewer  : MAJAK_RGB_CHATVIEWER = RGB( 96, 96, 96) = #606060
 *    abuse   : MAJAK_RGB_CHATABUSE  = RGB(255,  0,  0) = #ff0000
 *  行フォーマット (MJRoomWnd1.cpp:832): "{pix} : {string}"
 */
interface ChatMsg {
  id: string
  /** 表示名。内部 ID は pix に保持する。 */
  name: string
  pix?: string
  text: string
  /** 表示色。チャット受信時に送信者種別を見て決定する。 */
  color?: string
  bold?: boolean
}

/** ====================================================================
 * CMJRoomWnd 本体
 * ==================================================================== */
export default function RoomScreen() {
  const { channelId, roomId } = useParams<{   /* lobbyId は現在未使用 */
    channelId: string
    roomId: string
  }>()
  const navigate  = useNavigate()
  const location  = useLocation()
  const layoutMode = useOutgameLayoutMode()
  const isMobileIngame = layoutMode === 'mobileLandscape'
  const ingameLayoutMode = isMobileIngame ? 'mobileLandscape' : 'responsiveDesktop'

  /** AP-04 §8: ナビゲーション state からサーバー URL とモードを取得
   *   mode='create' : ルーム作成モード → send('c8e', ...)
   *   mode='enter'  : 既存ルーム入室 (default) → send('c14e', ...)
   *   mode='view'   : 満員/対局中ルーム観戦 → send('c18e', ...)
   */
  type RoomLocationState = {
    serverUrl?:   string
    mode?:        'create' | 'enter' | 'view' | 'auto'
    roomOption?:  string
    maxViewer?:   number
    moneyRate?:   number
    minMoney?:    number
    maxMoney?:    number
    isPrivate?:   boolean
    roomTitle?:   string
    roomPassword?: string
    trainingAiLevel?: 'Legacy' | 'Advanced'
    customBgId?: number
    customHaiId?: number
    customBoardType?: number
    autoEnterPayload?: Record<string, unknown>
    resumePlaying?: boolean
    skipEnterChannel?: boolean
    tournamentViewPayload?: Record<string, unknown>
  }
  const locState = (location.state ?? {}) as RoomLocationState
  const serverUrl  = locState.serverUrl ?? ''
  const createMode = locState.mode === 'create'

  const [chatLog,   setChatLog]   = useState<ChatMsg[]>([])
  const [chatText,  setChatText]  = useState('')
  const [roomTitle, setRoomTitle] = useState(locState.roomTitle ?? '')
  const [currentRoomOption, setCurrentRoomOption] = useState(locState.roomOption ?? '')
  const [statusLog, setStatusLog] = useState<ChatMsg[]>(LEGACY_STATUS_GUIDE)
  const [notice,    setNotice]    = useState<NoticeDisplay | null>(null)
  const [viewers,   setViewers]   = useState<ViewerEntry[]>([])
  const [channelMembers, setChannelMembers] = useState<ChannelMemberEntry[]>([])
  const [showInviteList, setShowInviteList] = useState(false)
  const pendingInviteTargetRef = useRef<string | null>(null)
  const pendingInviteTargetNameRef = useRef('')
  const channelMembersRef = useRef<ChannelMemberEntry[]>([])
  const [selectedViewer, setSelectedViewer] = useState<PlayerInfoDialogData | null>(null)
  const [isReady,   setIsReady]   = useState(false)
  const [players,   setPlayers]   = useState<PlayerInfoType[]>([])
  const [activeEmoticons, setActiveEmoticons] = useState<ActiveEmoticon[]>([])
  const [emoticonNow, setEmoticonNow] = useState(0)
  const [showCfg,    setShowCfg]   = useState(false)
  const [showAccuse, setShowAccuse] = useState(false)
  const [roomCfg,    setRoomCfg]   = useState<MJConfig>(() => loadMajakConfig())
  const fallbackSkin = useCustomSkinStore()
  const routeCustomBoardId = Number(locState.customBgId ?? 0)
  const routeCustomHaiId = Number(locState.customHaiId ?? 0)
  const routeCustomBoardType = Number(locState.customBoardType ?? 0)
  const customBoardId = routeCustomBoardId > 0 ? routeCustomBoardId : fallbackSkin.bgId
  const customHaiId = routeCustomHaiId > 0 ? routeCustomHaiId : fallbackSkin.haiId
  const customBoardType = routeCustomBoardType > 0 ? routeCustomBoardType : fallbackSkin.bgType
  const fullUiSkinId = getLegacyFullUiSkinId(customBoardId, customBoardType)
  const fullUiSkinSuffix = String(fullUiSkinId ?? customBoardId).padStart(2, '0')
  const hasFullCustomBoardSkin = fullUiSkinId != null
  const tengokuBoardSkin = isTengokuBoardSkin(customBoardId, customBoardType)
  const legacyPalette = getLegacyRoomPalette(tengokuBoardSkin)
  const boardSoundSkinId = getLegacyBoardSoundSkinId(customBoardId, customBoardType)
  const bgSkinSrc = (skinKey: string, baseKey = skinKey) => hasFullCustomBoardSkin
    ? `${IMG}/skin/${fullUiSkinId}/${skinKey}_${fullUiSkinSuffix}.png`
    : `${IMG}/${baseKey}.png`
  const bgSkinFallbackSrc = (_skinKey: string, baseKey = _skinKey) => hasFullCustomBoardSkin ? `${IMG}/${baseKey}.png` : undefined
  const customBoardSrc = getLegacyBoardImageUrl(customBoardId, customBoardType)
  const [gameSurroundColor, setGameSurroundColor] = useState('#176b2a')
  const gameSurroundStyle = {
    '--majak-game-surround-color': gameSurroundColor,
    backgroundColor: gameSurroundColor,
  } as CSSProperties
  useEffect(() => {
    const onBoardSurroundColor = (event: Event) => {
      const color = String((event as CustomEvent<{ color?: string }>).detail?.color ?? '')
      if (color) setGameSurroundColor(color)
    }
    window.addEventListener(GAME_BOARD_SURROUND_COLOR_EVENT, onBoardSurroundColor)
    return () => window.removeEventListener(GAME_BOARD_SURROUND_COLOR_EVENT, onBoardSurroundColor)
  }, [])
  const promptSrc = {
    waitEntry: bgSkinSrc('mj_promptWaitEntry'),
    pushReady: bgSkinSrc('mj_promptPushReady'),
    waitReady: bgSkinSrc('mj_promptWaitReady'),
    pushStart: bgSkinSrc('mj_promptPushStart'),
    waitStart: bgSkinSrc('mj_promptWaitStart'),
  }
  const [announceData, setAnnounceData] = useState<SlideAnnounceData | null>(null)
  const [pendingAnnounces, setPendingAnnounces] = useState<SlideAnnounceData[]>([])
  const [inlineGame, setInlineGame] = useState(false)
  const [inlineGameLoading, setInlineGameLoading] = useState(false)
  const useResponsiveWaitingLayout = !inlineGame
  const [gameLoadStep, setGameLoadStep] = useState<GameLoadStep>('server')
  const [gameLoadComplete, setGameLoadComplete] = useState(false)
  const [hanResData, setHanResData] = useState<HanResPlayer[] | null>(null)
  const [forcedHanResultDismissed, setForcedHanResultDismissed] = useState(false)
  const displayedHanResData = FORCE_HAN_RESULT_FOR_TEST && !forcedHanResultDismissed ? FORCED_HAN_RESULT : hanResData
  const [hanResFlags, setHanResFlags] = useState({ hasTor: false, hasTip: false, isViewer: false, isTournament: false })
  const [levelUp, setLevelUp] = useState<{ level: number; lentMoney: number } | null>(null)
  const [kyoResData, setKyoResData] = useState<KyoResData | null>(null)
  const [kyoResultAction, setKyoResultAction] = useState<{
    roomId: string
    seatOrder: number
    actionSeq?: number
    localDeadlineAt?: number
  } | null>(null)
  const [kyoResultSubmitted, setKyoResultSubmitted] = useState(false)
  const [kyoResultProgress, setKyoResultProgress] = useState<Record<number, { durationMs: number; localDeadlineAt: number; submitted: boolean }>>({})
  const [roomActionPending, setRoomActionPending] = useState(true)
  const [autoControl, setAutoControl] = useState<AutoControlState>({ prox: false, autoTap: false, autoPass: false, autoHora: false })
  const autoControlRef = useRef(autoControl)
  const [viewerHandHidden, setViewerHandHidden] = useState(false)
  const [hasChanceItem, setHasChanceItem] = useState(false)
  const [chanceReserved, setChanceReserved] = useState(false)
  /** 自分が方長かどうか (ChkHost 相当) */
  const [_amHost, setAmHost] = useState(false)
  const replayChannel = isReplayChannel(channelId)
  const trainingChannel = isTrainingChannel(channelId)
  const autoMatchingChannel = isAutoMatchingChannel(channelId)
  const chatEnabled = replayChannel || (!isDaniChannel(channelId) && readOptionDigit(currentRoomOption, 14, 0) !== 0)
  const viewerChatVisible = readOptionDigit(currentRoomOption, 7, 0) !== 0
  const viewerHandOpenEnabled = readOptionDigit(currentRoomOption, 6, 0) !== 0
  const chatRulesRef = useRef({ replayChannel, chatEnabled, viewerChatVisible })

  const chatLogRef   = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<HTMLInputElement>(null)
  const lastAccuseOpenedAtRef = useRef(0)
  useEffect(() => { configureMajakSound(roomCfg) }, [roomCfg])
  const statusLogRef = useRef<HTMLDivElement>(null)
  const inlineGameRef = useRef<HTMLDivElement>(null)
  const mobileIngameShellRef = useRef<HTMLDivElement>(null)
  const [mobileIngameScale, setMobileIngameScale] = useState(1)
  const [mobileHanResScale, setMobileHanResScale] = useState(1)
  const [mobileIngameOffsetX, setMobileIngameOffsetX] = useState(0)
  const [mobileIngameOffsetY, setMobileIngameOffsetY] = useState(0)
  const [mobileIngameChatOpen, setMobileIngameChatOpen] = useState(false)
  const [mobileIngameToolOpen, setMobileIngameToolOpen] = useState(false)
  const inlineGameActiveRef = useRef(false)
  const inlineGameEndedRef = useRef(false)
  const inlineGameLoadingVisibleRef = useRef(false)
  const gameLoadTimingRef = useRef({ active: false, startedAt: 0, lastAt: 0, step: 'server' as GameLoadStep, source: '' })
  const readyClickedAtRef = useRef(0)
  const gameReconnectActiveRef = useRef(Boolean(locState.resumePlaying) || getDocumentNavigationType() === 'reload')
  const viewersRef = useRef<ViewerEntry[]>([])
  const messageSeqRef = useRef(0)
  const gameNavigatedRef = useRef(false)
  const gameReportHandledRef = useRef(false)
  const roomActionSentKeyRef = useRef('')
  const reconnectSetupSerialRef = useRef(0)
  const exitingRoomRef = useRef(false)
  const roomJoinMessageMembersRef = useRef(new Set<string>())
  const proxyGuideShownRef = useRef(false)
  const roomEntryGuideShownRef = useRef(false)
  const gemGameGuideShownRef = useRef(false)
  const gemGameRef = useRef(0)
  const kyoResultTimerRef = useRef<number | null>(null)
  const gameEffectUntilRef = useRef(0)
  const emoticonSeqRef = useRef(0)
  const beginGameLoad = (source: string, trigger = '') => {
    const current = gameLoadTimingRef.current
    if (current.active && current.source === source) {
      console.info('[GameReconnect] existing progress preserved', { source, trigger, channelId, roomId })
      return
    }
    const now = performance.now()
    gameLoadTimingRef.current = { active: true, startedAt: now, lastAt: now, step: 'server', source }
    setGameLoadStep('server')
    setGameLoadComplete(false)
    console.info('[GameReconnect] start', {
      source,
      trigger,
      channelId,
      roomId,
      navigationType: getDocumentNavigationType(),
      startedAt: new Date().toISOString(),
    })
  }
  const advanceGameLoad = (step: GameLoadStep, details: Record<string, unknown> = {}) => {
    const timing = gameLoadTimingRef.current
    if (!timing.active) return
    const previousIndex = GAME_LOAD_STEPS.findIndex(item => item.id === timing.step)
    const nextIndex = GAME_LOAD_STEPS.findIndex(item => item.id === step)
    if (nextIndex <= previousIndex) return
    const now = performance.now()
    console.info('[GameReconnect] stage', {
      completedStep: timing.step,
      completedLabel: GAME_LOAD_STEPS[previousIndex]?.label ?? timing.step,
      previousDurationMs: Math.round(now - timing.lastAt),
      nextStep: step,
      nextLabel: GAME_LOAD_STEPS[nextIndex]?.label ?? step,
      totalElapsedMs: Math.round(now - timing.startedAt),
      source: timing.source,
      ...details,
    })
    timing.lastAt = now
    timing.step = step
    setGameLoadStep(step)
  }
  const finishGameLoad = (details: Record<string, unknown> = {}) => {
    const timing = gameLoadTimingRef.current
    if (!timing.active) return
    const now = performance.now()
    const stepIndex = GAME_LOAD_STEPS.findIndex(item => item.id === timing.step)
    console.info('[GameReconnect] complete', {
      completedStep: timing.step,
      completedLabel: GAME_LOAD_STEPS[stepIndex]?.label ?? timing.step,
      finalStageDurationMs: Math.round(now - timing.lastAt),
      totalDurationMs: Math.round(now - timing.startedAt),
      source: timing.source,
      completedAt: new Date().toISOString(),
      ...details,
    })
    console.info('[GameStartTiming] loading overlay completed', {
      roomId,
      totalDurationMs: Math.round(now - timing.startedAt),
      elapsedSinceReadyClickMs: readyClickedAtRef.current > 0 ? Math.round(now - readyClickedAtRef.current) : null,
      completedAt: new Date().toISOString(),
      ...details,
    })
    timing.active = false
    setGameLoadComplete(true)
  }
  const logRejoinProbe = (eventName: string, details: Record<string, unknown> = {}) => {
    if (!DEBUG_GAME) return
    console.info('[RoomScreen/RejoinProbe]', eventName, {
      channelId,
      roomId,
      mode: locState.mode ?? 'enter',
      resumePlaying: Boolean(locState.resumePlaying),
      inlineGame: inlineGameActiveRef.current,
      inlineGameLoading: inlineGameLoadingVisibleRef.current,
      roomActionSentKey: roomActionSentKeyRef.current,
      pix: useAuthStore.getState().player?.pix ?? '',
      ...details,
    })
  }
  const clearRoomActionPending = (reason: string) => {
    logRejoinProbe('room action pending cleared', { reason })
    setRoomActionPending(false)
  }
  const finishInlineGame = (reason: string) => {
    inlineGameEndedRef.current = true
    gameNavigatedRef.current = false
    inlineGameLoadingVisibleRef.current = false
    logRejoinProbe('inline game ended', { reason })
    suspendGame()
    setInlineGameLoading(false)
    setInlineGame(false)
    setRoomActionPending(false)
    setIsReady(false)
    setPlayers(prev => prev.map(player => ({ ...player, ready: false })))
    const resetAutoControl = { prox: false, autoTap: false, autoPass: false, autoHora: false }
    autoControlRef.current = resetAutoControl
    setAutoControl(resetAutoControl)
    setKyoResData(null)
    setKyoResultAction(null)
    setMobileIngameChatOpen(false)
    setMobileIngameToolOpen(false)
  }
  const holdInlineGameForResults = (reason: string) => {
    inlineGameEndedRef.current = true
    inlineGameLoadingVisibleRef.current = false
    logRejoinProbe('inline game held for results', { reason })
    freezeGameFrame()
    setInlineGameLoading(false)
    setKyoResData(null)
    setKyoResultAction(null)
    setKyoResultSubmitted(false)
    setKyoResultProgress({})
    setMobileIngameChatOpen(false)
    setMobileIngameToolOpen(false)
  }
  const startRoomActionPending = (command: string, details: Record<string, unknown> = {}) => {
    logRejoinProbe('room action pending started', { command, ...details })
    setRoomActionPending(true)
  }
  useEffect(() => {
    inlineGameActiveRef.current = inlineGame
    logRejoinProbe('inlineGame state changed', { inlineGame })
  }, [inlineGame])
  useEffect(() => {
    if (!inlineGame) {
      inlineGameLoadingVisibleRef.current = false
      setInlineGameLoading(false)
      return
    }
    inlineGameLoadingVisibleRef.current = true
    setInlineGameLoading(true)
  }, [inlineGame])
  useEffect(() => {
    const setInlineGameLoadingVisible = (visible: boolean) => {
      if (visible) {
        if (inlineGameLoadingVisibleRef.current) return
        inlineGameLoadingVisibleRef.current = true
        setInlineGameLoading(true)
        return
      }
      inlineGameLoadingVisibleRef.current = false
      setInlineGameLoading(false)
    }
    const onSync = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean; reason?: string }>).detail ?? {}
      const active = Boolean(detail.active)
      logRejoinProbe('GAME_SYNC_EVENT', { active, reason: detail.reason ?? '' })
      if (active) {
        setInlineGameLoadingVisible(true)
        return
      }
      advanceGameLoad('ready', { syncReason: detail.reason ?? '' })
      finishGameLoad({ syncReason: detail.reason ?? '' })
      setInlineGameLoadingVisible(false)
    }
    const onLoadProgress = (event: Event) => {
      const detail = (event as CustomEvent<{ step?: string } & Record<string, unknown>>).detail ?? {}
      if (typeof detail.step !== 'string') return
      if (!GAME_LOAD_STEPS.some(step => step.id === detail.step)) return
      advanceGameLoad(detail.step as GameLoadStep, detail)
    }
    window.addEventListener(GAME_SYNC_EVENT, onSync)
    window.addEventListener(GAME_LOAD_PROGRESS_EVENT, onLoadProgress)
    return () => {
      window.removeEventListener(GAME_SYNC_EVENT, onSync)
      window.removeEventListener(GAME_LOAD_PROGRESS_EVENT, onLoadProgress)
    }
  }, [])

  useEffect(() => {
    const onGameEffectWindow = (event: Event) => {
      const detail = (event as CustomEvent<{ durationMs?: number }>).detail ?? {}
      const durationMs = Number(detail.durationMs ?? 0)
      if (!Number.isFinite(durationMs) || durationMs <= 0) return
      gameEffectUntilRef.current = Math.max(gameEffectUntilRef.current, performance.now() + durationMs)
    }
    window.addEventListener(GAME_EFFECT_WINDOW_EVENT, onGameEffectWindow)
    return () => window.removeEventListener(GAME_EFFECT_WINDOW_EVENT, onGameEffectWindow)
  }, [])

  useEffect(() => {
    if (announceData || pendingAnnounces.length === 0) return
    const [next, ...remaining] = pendingAnnounces
    setPendingAnnounces(remaining)
    setAnnounceData(next)
  }, [announceData, pendingAnnounces])
  useEffect(() => { viewersRef.current = viewers }, [viewers])
  useEffect(() => {
    if (!mobileIngameChatOpen) return
    window.requestAnimationFrame(() => {
      if (chatLogRef.current) chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight
      if (statusLogRef.current) statusLogRef.current.scrollTop = statusLogRef.current.scrollHeight
    })
  }, [mobileIngameChatOpen])
  useEffect(() => {
    if (chatEnabled) return
    setMobileIngameChatOpen(false)
  }, [chatEnabled])
  useEffect(() => {
    if (!isMobileIngame) {
      setMobileIngameScale(1)
      setMobileHanResScale(1)
      setMobileIngameOffsetX(0)
      setMobileIngameOffsetY(0)
      return
    }
    const update = () => {
      const rect = mobileIngameShellRef.current?.getBoundingClientRect()
      if (!rect) {
        const viewportHanResScale = Math.min(1, window.innerWidth / MOBILE_HAN_RES_W, window.innerHeight / MOBILE_HAN_RES_H)
        setMobileHanResScale(Number.isFinite(viewportHanResScale) && viewportHanResScale > 0 ? viewportHanResScale : 1)
        setMobileIngameScale(1)
        setMobileIngameOffsetX(0)
        setMobileIngameOffsetY(0)
        return
      }
      const focusWidth = inlineGame ? MOBILE_INGAME_FOCUS_W : ROOM_W
      const availableHeight = rect.height
      const focusHeight = inlineGame ? MOBILE_INGAME_VISIBLE_H : ROOM_H
      const nextScale = inlineGame ? rect.width / focusWidth : Math.min(rect.width / focusWidth, availableHeight / focusHeight)
      const desiredHanResScale = Math.min(1, rect.width / MOBILE_HAN_RES_W, rect.height / MOBILE_HAN_RES_H)
      const nextHanResScale = inlineGame && Number.isFinite(nextScale) && nextScale > 0
        ? desiredHanResScale / nextScale
        : desiredHanResScale
      const nextOffsetX = 0
      const nextOffsetY = inlineGame ? MOBILE_INGAME_OFFSET_Y : Math.min(0, availableHeight - ROOM_H * nextScale)
      setMobileIngameScale(Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1)
      setMobileHanResScale(Number.isFinite(nextHanResScale) && nextHanResScale > 0 ? nextHanResScale : 1)
      setMobileIngameOffsetX(Number.isFinite(nextOffsetX) ? nextOffsetX : 0)
      setMobileIngameOffsetY(Number.isFinite(nextOffsetY) ? nextOffsetY : 0)
    }
    update()
    const observer = new ResizeObserver(update)
    if (mobileIngameShellRef.current) observer.observe(mobileIngameShellRef.current)
    window.addEventListener('resize', update)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [inlineGame, isMobileIngame])
  /** \u73fe\u5728\u306e players \u3092 SignalR \u30cf\u30f3\u30c9\u30e9\u304b\u3089\u53c2\u7167\u3059\u308b\u305f\u3081\u306e ref
   *  (useEffect \u4f9d\u5b58\u914d\u5217\u306b players \u3092\u5165\u308c\u308b\u3068\u30b3\u30fc\u30eb\u30d0\u30c3\u30af\u3092\u6bce\u56de\u518d\u767b\u9332\u3057\u3066\u3057\u307e\u3046\u305f\u3081)\n   */
  const playersRef = useRef<PlayerInfoType[]>([])
  useEffect(() => { playersRef.current = players }, [players])
  useEffect(() => {
    chatRulesRef.current = { replayChannel, chatEnabled, viewerChatVisible }
  }, [replayChannel, chatEnabled, viewerChatVisible])

  useEffect(() => {
    if (!inlineGame || !inlineGameRef.current) return
    const myPix = useAuthStore.getState().player?.pix ?? ''
    const me = playersRef.current.find(p => p.playerId === myPix)
    const isViewer = locState.mode === 'view' || (playersRef.current.length > 0 && !me)
    logRejoinProbe('createGame', {
      myOdr: me?.pos,
      isViewer,
      playersCount: playersRef.current.length,
      roomOption: currentRoomOption,
      layoutMode: ingameLayoutMode,
      skipInitialRoomEnter: true,
      requestInitialGameResync: gameReconnectActiveRef.current,
    })
    createGame(inlineGameRef.current, {
      mode: 'game',
      layoutMode: ingameLayoutMode,
      roomId: roomId ?? '',
      roomName: roomTitle,
      myOdr: me?.pos,
      isViewer,
      players: playersRef.current as unknown as Array<Record<string, unknown>>,
      roomOption: currentRoomOption,
      inputConfig: { nSelPasKey: roomCfg.nSelPasKey },
      recordPaifuMode: roomCfg.nChkREC,
      assistConfig: {
        bChkTap: roomCfg.bChkTap,
        bChkPai: roomCfg.bChkPai,
        bChkTnp: roomCfg.bChkTnp,
        bChkHor: roomCfg.bChkHor,
      },
      trainingRecommendations: trainingChannel && !isViewer,
      customBgId: customBoardId,
      customBoardType,
      customHaiId,
      gemGame: gemGameRef.current,
      skipInitialRoomEnter: true,
      requestInitialGameResync: gameReconnectActiveRef.current,
    })
    return () => suspendGame()
  }, [inlineGame, roomId, customBoardId, customHaiId, ingameLayoutMode, locState.mode])

  useEffect(() => () => destroyGame(), [])

  const nextMessageId = () => `${Date.now()}-${messageSeqRef.current++}`

  const putRoomMessage = (text: string, color: string, bold = false) => {
    setChatLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text, color, bold }))
  }

  const displayNameForPix = (pix: string): string => {
    if (!pix) return ''
    const player = playersRef.current.find(item => item.playerId === pix)
    if (player?.name) return player.name
    const viewer = viewersRef.current.find(item => item.pix === pix)
    if (viewer?.name) return viewer.name
    const member = channelMembersRef.current.find(item => item.pix === pix)
    return member?.name || pix
  }

  const putRoomJoinMessage = (pix: string, player = true, displayName?: string) => {
    if (!pix || roomJoinMessageMembersRef.current.has(pix)) return
    roomJoinMessageMembersRef.current.add(pix)
    putRoomMessage(`${displayName || displayNameForPix(pix)}様が入室しました。`, legacyPalette.roomJoin, player)
  }

  const putRoomEntryGuideMessages = () => {
    if (roomEntryGuideShownRef.current) return
    roomEntryGuideShownRef.current = true
    setChatLog(prev => appendRoomLog(prev,
      ...(isDaniChannel(channelId) ? [{ id: nextMessageId(), name: '', text: LEGACY_CANCEL_GUIDE_DANI, color: legacyPalette.notice, bold: true }] : []),
      { id: nextMessageId(), name: '', text: LEGACY_CANCEL_GUIDE_STOP, color: legacyPalette.notice },
      { id: nextMessageId(), name: '', text: LEGACY_CANCEL_GUIDE_PASS, color: legacyPalette.normal, bold: true },
    ))
  }

  useEffect(() => {
    const onGameStatus = (event: Event) => {
      const detail = (event as CustomEvent<{ text?: string; color?: string; bold?: boolean }>).detail ?? {}
      const text = String(detail.text ?? '')
      if (text) setStatusLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text, color: applyTengokuTextColor(detail.color, tengokuBoardSkin), bold: Boolean(detail.bold) }))
    }
    window.addEventListener(GAME_STATUS_EVENT, onGameStatus)
    return () => window.removeEventListener(GAME_STATUS_EVENT, onGameStatus)
  }, [])

  const putProxyGuideStatus = () => {
    if (proxyGuideShownRef.current) return
    proxyGuideShownRef.current = true
    const guide = extractSubId(channelId)[2] === 'H' ? LEGACY_TOURNAMENT_LINEOFF_GUIDE : LEGACY_PROXY_GUIDE
    setStatusLog(prev => appendRoomLog(prev,
      ...guide.map(line => ({ id: nextMessageId(), name: '', text: line, color: legacyPalette.normal, bold: true })),
    ))
  }

  const putGemGameStatus = (value: unknown) => {
    if (gemGameGuideShownRef.current) return
    const text = gemGameStatusText(value)
    if (!text) return
    gemGameGuideShownRef.current = true
    setStatusLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text, color: legacyPalette.normal, bold: true }))
  }

  const exitRoomToLobby = async (seatPos: number | undefined, isViewer = false) => {
    if (exitingRoomRef.current) return
    exitingRoomRef.current = true
    void SignalR.send('c9e', buildExitRoomPayload(useAuthStore.getState().player, seatPos, isViewer)).catch(() => {
      window.sessionStorage.setItem(ABANDON_ROOM_STORAGE_KEY, JSON.stringify({
        channelId: channelId ?? '',
        roomId: Number(roomId ?? 0),
      }))
    })
    navigate(`/channel/${channelId ?? ''}/lobby`, { state: { leavingRoom: true } })
  }

  /** SignalR 接続 & ルーム参加 */
  useEffect(() => {
    let mounted = true
    let pendingChannelEntryHandler: SignalR.MessageHandler | null = null
    let roomEntryFailureHandled = false
    const effectInstance = ++reconnectSetupSerialRef.current
    console.info('[GameReconnect] RoomScreen connection effect mounted', {
      effectInstance,
      channelId,
      roomId,
      mode: locState.mode ?? 'enter',
      resumePlaying: Boolean(locState.resumePlaying),
      locationKey: location.key,
      navigationType: getDocumentNavigationType(),
      roomActionSentKey: roomActionSentKeyRef.current,
      existingConnectionId: SignalR.getConnection()?.connectionId,
      existingConnectionState: SignalR.getConnection()?.state ?? 'missing',
    })

    const waitForChannelEntryResponse = () => new Promise<Record<string, unknown>>(resolve => {
      if (pendingChannelEntryHandler) SignalR.off('c1e', pendingChannelEntryHandler)
      const onChannelEntered = (data: Record<string, unknown>) => {
        SignalR.off('c1e', onChannelEntered)
        pendingChannelEntryHandler = null
        resolve(data)
      }
      pendingChannelEntryHandler = onChannelEntered
      SignalR.on('c1e', onChannelEntered)
    })

    const enterChannel = async (payload: Record<string, unknown>) => {
      console.info('[GameReconnect] RoomScreen c1e request start', {
        effectInstance,
        connectionId: SignalR.getConnection()?.connectionId,
        channelId,
        roomId,
      })
      const responsePromise = waitForChannelEntryResponse()
      try {
        const [, response] = await Promise.all([SignalR.send('c1e', payload), responsePromise])
        const succeeded = Number(response.result) === 1 || response.k1e === 'v1e'
        console.info('[GameReconnect] channel entry response', {
          effectInstance,
          connectionId: SignalR.getConnection()?.connectionId,
          succeeded,
          error: response.error,
          message: response.k2e ?? response.message,
        })
        if (!succeeded) {
          throw new Error(String(response.k2e ?? response.message ?? 'チャンネルへの再接続に失敗しました'))
        }
      } catch (error) {
        if (pendingChannelEntryHandler) {
          SignalR.off('c1e', pendingChannelEntryHandler)
          pendingChannelEntryHandler = null
        }
        throw error
      }
    }

    const absorbAlreadyInRoom = (data: Record<string, unknown>) => {
      const message = String(data.k2e ?? data.message ?? '')
      const responseRoomId = Number(data.k42e ?? data.roomId ?? roomId ?? 0)
      const currentRoomId = Number(roomId ?? 0)
      if (!message.includes('既に') || !message.includes('入室')) return false
      if (currentRoomId > 0 && responseRoomId > 0 && responseRoomId !== currentRoomId) return false
      logRejoinProbe('absorb already-in-room', { responseRoomId, message, data })
      if (DEBUG_GAME) console.info('[RoomScreen] already in current room; keeping room screen', { roomId, responseRoomId, message, data })
      if (gameReconnectActiveRef.current) {
        console.warn('[GameReconnect] already-in-room response rejected as reconnect proof', {
          connectionId: SignalR.getConnection()?.connectionId,
          currentRoomId,
          responseRoomId,
          message,
        })
        return false
      }
      void SignalR.send('c16e', {}).catch(() => {})
      return true
    }

    const handleRoomEntryFailure = (data: Record<string, unknown>, fallbackMessage?: string) => {
      if (roomEntryFailureHandled) return
      roomEntryFailureHandled = true
      const responseRoomId = Number(data.k42e ?? data.roomId ?? 0)
      window.sessionStorage.setItem(ABANDON_ROOM_STORAGE_KEY, JSON.stringify({
        channelId: channelId ?? '',
        roomId: responseRoomId > 0 ? responseRoomId : Number(roomId ?? 0),
        fatalRoomError: true,
      }))
      navigate(`/channel/${channelId ?? ''}/lobby`, { replace: true })
      const isExistingRoomEntry = !createMode && locState.mode !== 'auto' && locState.mode !== 'view'
      if (isExistingRoomEntry) return
      const rawMessage = data.k2e ?? data.message
      const message = typeof rawMessage === 'string' && rawMessage.trim() && rawMessage.trim() !== 'エラー'
        ? rawMessage.trim()
        : fallbackMessage ?? roomEnterErrorMessage(data)
      void showError(message)
    }

    /**
     * room:enter 応答 — RoomEnterRoomCommand
     * レガシー ProcessCommand_EnterRoom:
     *   result == failure → エラー表示 (メッセージあり)
     * 応答フィールド: result(1=成功), roomId, roomOption, state
     */
    const onRoomEnter = (data: Record<string, unknown>) => {
      console.info('[GameReconnect] RoomScreen c14e response received', {
        effectInstance,
        mounted,
        connectionId: SignalR.getConnection()?.connectionId,
        roomActionSentKey: roomActionSentKeyRef.current,
        result: data.result ?? data.k1e,
        state: data.state,
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
      })
      if (!mounted) {
        console.warn('[GameReconnect] RoomScreen c14e response ignored: effect unmounted', {
          effectInstance,
          connectionId: SignalR.getConnection()?.connectionId,
        })
        return
      }
      clearRoomActionPending('c14e response')
      logRejoinProbe('c14e room enter response', {
        result: data.result,
        k1e: data.k1e,
        state: data.state,
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
        data,
      })
      if (Number(data.result) !== 1) {
        if (absorbAlreadyInRoom(data)) return
        handleRoomEntryFailure(data)
        return
      }
      const nextRoomTitle = data.k45e ?? data.roomTitle
      if (typeof nextRoomTitle === 'string') setRoomTitle(nextRoomTitle)
      const nextRoomOption = data.k46e ?? data.roomOption
      if (typeof nextRoomOption === 'string') setCurrentRoomOption(nextRoomOption)
      setHasChanceItem(asLegacyBool(data.canUseChanceItem))
      setChanceReserved(asLegacyBool(data.k118e ?? data.reserveChance))
      const player = readRoomPlayer(data)
      if (player) {
        putRoomJoinMessage(player.playerId, player.pos >= 0, player.name)
        setPlayers(prev => prev.some(p => p.playerId === player.playerId)
          ? prev.map(p => p.playerId === player.playerId ? { ...p, ...player } : p)
          : [...prev, player])
        const myPix = useAuthStore.getState().player?.pix ?? ''
        if (player.playerId === myPix && player.pos >= 0) {
          putProxyGuideStatus()
          putRoomEntryGuideMessages()
        }
      }
      if (Number(data.state ?? -1) === 2) {
        gameReportHandledRef.current = false
        setHanResData(null)
        setLevelUp(null)
        setAnnounceData(null)
        setPendingAnnounces([])
        inlineGameLoadingVisibleRef.current = true
        setInlineGameLoading(true)
        setInlineGame(true)
      } else {
        finishGameLoad({ reason: 'waiting-room-ready' })
      }
    }
    SignalR.on('c14e', onRoomEnter)

    const onReserveChance = (data: Record<string, unknown>) => {
      if (!mounted) return
      const success = Number(data.result) === 1 || data.k1e === 'v1e'
      setHasChanceItem(asLegacyBool(data.canUseChanceItem))
      setChanceReserved(success && asLegacyBool(data.k118e ?? data.reserveChance))
    }
    SignalR.on('c55e', onReserveChance)

    /**
     * room:connect_error — ルーム入室エラー
     * 応答フィールド: k42e, k2e, failcode
     */
    const onRoomEnterError = (data: Record<string, unknown>) => {
      if (!mounted) return
      clearRoomActionPending('c34e response')
      logRejoinProbe('c34e room enter error', {
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
        failcode: data.failcode ?? data.failCode,
        data,
      })
      if (absorbAlreadyInRoom(data)) return
      handleRoomEntryFailure(data)
    }
    SignalR.on('c34e', onRoomEnterError)

    /**
     * room:member_list (Cmd.MemberList) — アクティブプレイヤーリスト
     * レガシー AddToParser_GetMemberListResponse 相当
    * 応答フィールド: members[{pix, nickName, avatarId, playerPos, ...}]
     */
    const onPlayerList = (data: Record<string, unknown>) => {
      if (!mounted) return
      const legacyCount = Number(data.k25e ?? data.count ?? 0)
      const legacyMembers = Array.from({ length: legacyCount }, (_, index): Record<string, unknown> => {
        const pix = data[`k3e${index}`]
        const playerType = data[`k57e${index}`]
        return {
          pix,
          nickName: data[`mjkk34e${index}`] ?? data[`k8e${index}`] ?? pix,
          name: data[`k8e${index}`] ?? data[`mjkk34e${index}`] ?? pix,
          avatarId: data[`k7e${index}`],
          sex: data[`k11e${index}`],
          playerType,
          playerPos: data[`k58e${index}`],
          seatPos: data[`k58e${index}`],
          rating: data[`k31e${index}`],
          slevel: data[`k32e${index}`],
          nlevel: data[`k33e${index}`],
          isProxy: data[`isProxy${index}`],
          skillCnt: data[`skillCnt${index}`] ?? data[`skillCount${index}`],
          ready: data[`ready${index}`] ?? data[`isReady${index}`] ?? data[`okButton${index}`],
          isHost: pix === data.k50e,
          isViewer: playerType === 'v5e',
        }
      }).filter(member => member.pix != null && String(member.pix) !== '')
      const members = Array.isArray(data.members) && data.members.length > 0
        ? data.members as Array<Record<string, unknown>>
        : legacyMembers
      const viewerMembers = members.filter(isViewerPacket)
      const list = members.filter(m => !isViewerPacket(m)).map(readRoomPlayer).filter(p => p != null)
      const previousReadyById = new Map(playersRef.current.map(player => [player.playerId, player.ready]))
      const previousReadyByPos = new Map(playersRef.current.map(player => [player.pos, player.ready]))
      const uniquePlayers = [...new Map(list.filter(p => p.playerId).map(p => [p.playerId, p])).values()]
        .map(player => ({
          ...player,
          ready: player.ready || previousReadyById.get(player.playerId) || previousReadyByPos.get(player.pos) || false,
        }))
      playersRef.current = uniquePlayers
      setPlayers(uniquePlayers)
      const entries = viewerMembers.map(toViewerEntry).filter(v => v.pix)
      setViewers(entries)
      // ChkHost 相当: 自分が方長か判定
      const myPix = useAuthStore.getState().player?.pix ?? ''
      setAmHost(uniquePlayers.some(p => p.playerId === myPix && p.isHost))
    }
    SignalR.on('c16e', onPlayerList)

    /** member:get_list — 招待ボタンから開くメンバーリスト用 */
    const onChannelMemberList = (data: Record<string, unknown>) => {
      if (!mounted) return
      if (Number(data.result) !== 1 && data.k1e !== 'v1e') return
      const count = Number(data.k25e ?? data.count ?? 0)
      const legacyList: Array<Record<string, unknown>> = Array.from({ length: count }, (_, index): Record<string, unknown> | null => {
        const raw = data[`k3e${index}`]
        if (typeof raw !== 'string' || raw === '') return null
        const parts = raw.split('\t')
        if (parts.length >= 22) {
          return {
            pix: parts[0] ?? '',
            name: parts[2] || parts[27] || parts[0] || '',
            rating: Number(parts[12] ?? 0),
            avatarId: parts[1] || undefined,
            sex: parts[3] || undefined,
            slevel: parts[13] === ' ' ? undefined : parts[13],
            location: parts[5] || undefined,
            roomId: undefined,
          }
        }
        if (parts.length >= 12) {
          return {
            pix: parts[0] ?? '',
            name: parts[17] && parts[17] !== ' ' ? parts[17] : parts[0] ?? '',
            rating: Number(parts[7] ?? 0),
            avatarId: undefined,
            sex: parts[1] || undefined,
            slevel: undefined,
            location: parts[2] || undefined,
            roomId: undefined,
          }
        }
        return null
      }).filter((member): member is Record<string, unknown> => member != null && String(member.pix ?? '') !== '')
      const list: Array<Record<string, unknown>> = Array.isArray(data.members)
        ? data.members as Array<Record<string, unknown>>
        : legacyList
      const myPix = useAuthStore.getState().player?.pix ?? ''
      const availableMembers = list
        .map(m => ({
          pix: String(m.k3e ?? m.pix ?? m['member' + 'Id'] ?? ''),
          name:     String(m.k8e ?? m.nickname ?? m.name ?? ''),
          rating:   Number(m.k31e ?? m.rating ?? 0),
          gamMoney: Number(m.k34e ?? m.gammoney ?? 0),
          avatarId: m.k7e != null || m.avatarId != null ? String(m.k7e ?? m.avatarId) : undefined,
          sex:      m.k11e != null || m.sex != null ? String(m.k11e ?? m.sex) : undefined,
          slevel:   m.k32e != null || m.slevel != null ? String(m.k32e ?? m.slevel) : undefined,
          location: m.k12e != null || m.location != null ? String(m.k12e ?? m.location) : undefined,
          roomId:   m.k42e != null || m.roomId != null ? Number(m.k42e ?? m.roomId) : undefined,
        }))
        .filter(m => m.pix && m.pix !== myPix && (m.roomId == null || m.roomId <= 0) && (!m.location || m.location === 'ロビー'))
      channelMembersRef.current = availableMembers
      setChannelMembers(availableMembers)
    }
    SignalR.on('c7e', onChannelMemberList)

    const isViewerPacket = (data: Record<string, unknown>) => {
      const playerType = data.k57e ?? data.playerType
      return playerType === 'viewer' || playerType === 'v5e' || playerType === 2 || playerType === '2'
    }

    const isRoomMemberPacket = (data: Record<string, unknown>) =>
      data.k57e != null || data.playerType != null ||
      data.k58e != null || data.playerPos != null || data.seatPos != null

    const upsertViewer = (data: Record<string, unknown>) => {
      const entry = toViewerEntry(data)
      if (!entry.pix) return
      setViewers(prev => [...prev.filter(v => v.pix !== entry.pix), entry])
    }

    const toViewerEntry = (data: Record<string, unknown>): ViewerEntry => ({
      pix: String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? ''),
      name:     String(data.mjkk34e ?? data.k8e ?? data.nickName ?? data.nickname ?? data.name ?? ''),
      avatarId: data.k7e != null || data.avatarId != null ? String(data.k7e ?? data.avatarId) : undefined,
      sex:      data.k11e != null || data.sex != null ? String(data.k11e ?? data.sex) : undefined,
      slevel:   data.k32e != null || data.slevel != null ? String(data.k32e ?? data.slevel) : undefined,
      dan:      data.dan != null ? String(data.dan) : toDanName(Number(data.gradeCurrLevel ?? -1)),
      rating:   Number(data.k31e ?? data.rating ?? 0),
      playerPos: data.k58e != null || data.playerPos != null ? Number(data.k58e ?? data.playerPos) : undefined,
    })

    const toDanName = (gradeLevel: number) => [
      '10級', '9級', '8級', '7級', '6級', '5級', '4級', '3級', '2級', '1級',
      '初段', '二段', '三段', '四段', '五段', '六段', '七段', '八段', '九段', '十段',
    ][gradeLevel] ?? ''

    const removeViewer = (data: Record<string, unknown>) => {
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      if (!pix) return
      setViewers(prev => prev.filter(v => v.pix !== pix))
    }

    /**
     * room:member_joined — AdditionalFunctionOfClientRoomAddMember 相当
     * プレイヤー入室時: メンバー追加 + ステータスメッセージ
    * 応答フィールド: pix, nickName, playerPos, isHost
     */
    const onMemberJoined = (data: Record<string, unknown>) => {
      if (!mounted) return
      if (!isRoomMemberPacket(data)) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      if (isViewerPacket(data)) {
        const viewerName = String(data.mjkk34e ?? data.k8e ?? data.nickName ?? data.nickname ?? data.name ?? '')
        upsertViewer(data)
        putRoomJoinMessage(pix, false, viewerName)
        return
      }
      const player = readRoomPlayer(data)
      if (player) {
        putRoomJoinMessage(player.playerId, true, player.name)
        playMajakSid(SID_JOIN, boardSoundSkinId ? { skinId: boardSoundSkinId } : {})
        setPlayers(prev => [...prev.filter(p => p.playerId !== player.playerId), player])
        const myPix = useAuthStore.getState().player?.pix ?? ''
        if (player.playerId === myPix && player.pos >= 0) {
          putProxyGuideStatus()
          putRoomEntryGuideMessages()
        }
      }
      // プレイヤーリスト再取得 (AdditionalFunctionOfClientRoomAddMember で m_tbl.AddMember() 相当)
      void SignalR.send('c16e', {}).catch(() => {})
    }
    SignalR.on('c5e', onMemberJoined)

    /**
     * room:member_left — AdditionalFunctionOfClientRoomDeleteMember 相当
     * プレイヤー退室時: メンバー削除 + ステータスメッセージ
     */
    const onMemberLeft = (data: Record<string, unknown>) => {
      if (!mounted) return
      if (!isRoomMemberPacket(data)) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      const isPlaying = Boolean(data.isPlaying ?? data.playing ?? false)
      const displayName = displayNameForPix(pix)
      const msg = isPlaying
        ? `${displayName}様の接続が切れました。`  // IDS_MAJAK_ROOMDROP 相当
        : `${displayName}様が退室しました。`      // IDS_MAJAK_ROOMEXIT 相当
      if (pix) roomJoinMessageMembersRef.current.delete(pix)
      if (isViewerPacket(data)) {
        removeViewer(data)
        putRoomMessage(msg, legacyPalette.roomExit)
        return
      }
      putRoomMessage(msg, isPlaying ? legacyPalette.roomDrop : legacyPalette.roomExit, true)
      playMajakSid(SID_EXIT, boardSoundSkinId ? { skinId: boardSoundSkinId } : {})
      setPlayers(prev => prev.filter(p => p.playerId !== pix))
      // ChkHost: 方長が変わったかもしれないので再取得
      void SignalR.send('c16e', {}).catch(() => {})
    }
    SignalR.on('c6e', onMemberLeft)

    /** mjkc5e (Cmd.AutoExitRoom) — レガシー AutoExitRoom 通知 */
    const onAutoExitRoom = (data: Record<string, unknown>) => {
      if (!mounted) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      const myPix = useAuthStore.getState().player?.pix ?? ''
      if (pix === '' || pix === myPix) {
        const message = data.k2e ?? data.message
        if (typeof message === 'string' && message.length > 0) showError(message)
        navigate(`/channel/${channelId ?? ''}/lobby`)
        return
      }
      setPlayers(prev => prev.filter(p => p.playerId !== pix))
      setViewers(prev => prev.filter(v => v.pix !== pix))
    }
    SignalR.on('mjkc5e', onAutoExitRoom)

    /**
     * mjkroom (Cmd.RoomState) — ルーム状態変化通知
     * レガシー CMJRoomWnd は created/joined などの内部 action を履歴表示しない。
     */
    const onRoomState = (_data: Record<string, unknown>) => {
      if (!mounted) return
      const action = String(_data.action ?? '')
      if (action !== 'game_ended') return
      finalizePaifuRecording({ roomName: roomTitle })
      if (autoControlRef.current.prox) {
        const myPix = useAuthStore.getState().player?.pix ?? ''
        const seatPos = playersRef.current.find(player => player.playerId === myPix)?.pos
        void exitRoomToLobby(seatPos)
        return
      }
      if (gameReportHandledRef.current) return
      finishInlineGame('mjkroom game_ended')
    }
    SignalR.on('mjkroom', onRoomState)

    /**
     * c32e (G::commandGameReport) — レガシー CMJRoomWnd::ProcessRoomGameReportCommand。
     * 最終結果を受け取った時点で CMJRoomWnd は MODE_PLAYEND に入り、卓側の同期処理を止める。
     */
    const onGameReport = (data: Record<string, unknown>) => {
      if (!mounted) return
      const reportRoomId = String(data.roomId ?? data.k42e ?? '')
      if (roomId && reportRoomId && roomId !== reportRoomId) return
      if (Number(data.result) !== 1) {
        finishInlineGame('c32e game report failure')
        setHanResData(null)
        setStatusLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text: 'ゲーム結果の取得に失敗しました。', color: legacyPalette.error, bold: true }))
        return
      }
      if (gameReportHandledRef.current) return
      gameReportHandledRef.current = true
      const myPix = useAuthStore.getState().player?.pix ?? ''
      const players = readHanResPlayers(data, myPix)
      const shouldAutoExit = autoControlRef.current.prox
      const seatPos = playersRef.current.find(player => player.playerId === myPix)?.pos
      const myResult = players.find(player => player.isMe)
      if (myResult) {
        useGamePlayerStore.getState().setData({
          ...(typeof myResult.gameMoney === 'number' && Number.isFinite(myResult.gameMoney) ? { gamMoney: myResult.gameMoney } : {}),
          ...(typeof myResult.nlevel === 'number' && Number.isFinite(myResult.nlevel) ? { nlevel: myResult.nlevel } : {}),
          ...(myResult.levelName ? { slevel: myResult.levelName } : {}),
          ...(typeof myResult.gemCount === 'number' && Number.isFinite(myResult.gemCount) ? { gemCount: myResult.gemCount } : {}),
        })
      }
      finalizePaifuRecording({
        roomName: roomTitle,
        result: myResult ? `${myResult.rank + 1}位` : '',
        members: players.map(player => ({ name: player.name, result: `${player.rank + 1}位` })),
      })
      if (shouldAutoExit) {
        void exitRoomToLobby(seatPos)
        return
      }
      const isViewerResult = locState.mode === 'view'
        || !playersRef.current.some(player => player.playerId === myPix)
      const nextHanResFlags = {
        hasTor: Boolean(data.hasTor),
        hasTip: Boolean(data.hasTip),
        isViewer: isViewerResult,
        isTournament: Boolean(data.isTournament),
      }
      setHanResFlags(nextHanResFlags)
      const currentLevel = myResult?.nlevel
      const previousLevel = myResult?.prevNlevel
      const shouldShowLevelUp = !nextHanResFlags.isViewer
        && !nextHanResFlags.isTournament
        && currentLevel !== undefined
        && previousLevel !== undefined
        && currentLevel > previousLevel
      setLevelUp(shouldShowLevelUp ? { level: currentLevel, lentMoney: myResult?.lentMoney ?? 0 } : null)
      gameEndStatusLines(data).forEach(line => {
        setStatusLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text: line, color: legacyPalette.normal, bold: true }))
      })
      holdInlineGameForResults('c32e game report')
      setHanResData(players)
    }
    SignalR.on('c32e', onGameReport)

    const onGamePlay = (data: Record<string, unknown>) => {
      if (!mounted) return
      if (data.playType === 'MJPID_INIHAN' || data.playType === 'MJPID_INIKYO') {
        if (data.playType === 'MJPID_INIHAN') gameReportHandledRef.current = false
        if (kyoResultTimerRef.current !== null) window.clearTimeout(kyoResultTimerRef.current)
        kyoResultTimerRef.current = null
        setKyoResData(null)
        setKyoResultAction(null)
        setKyoResultSubmitted(false)
        setKyoResultProgress({})
        return
      }
      if (data.playType !== 'MJPID_ENDKYO' || !Array.isArray(data.players)) return
      if (kyoResultTimerRef.current !== null) window.clearTimeout(kyoResultTimerRef.current)
      setKyoResultAction(null)
      setKyoResultSubmitted(false)
      setKyoResultProgress({})
      const showKyoResult = () => {
        if (!mounted) return
        kyoResultTimerRef.current = null
        setKyoResData(data as unknown as KyoResData)
      }
      const delayData = {
        ...data,
        players: data.players.map((value, index) => {
          if (value == null || typeof value !== 'object') return value
          const playerResult = value as Record<string, unknown>
          const seatPos = asNumber(playerResult.seatPos ?? playerResult.odr, index)
          const player = playersRef.current.find(item => item.pos === seatPos) ?? playersRef.current[index]
          return { ...playerResult, trickTitle: player?.trickTitle }
        }),
      }
      queueMicrotask(() => {
        if (!mounted) return
        const animationsEnabled = document.visibilityState === 'visible'
        const resultEffectDuration = animationsEnabled ? getLegacyKyoResultDelayMs(delayData) : 0
        const activeEffectDuration = animationsEnabled
          ? Math.max(0, gameEffectUntilRef.current - performance.now())
          : 0
        const effectDuration = Math.max(resultEffectDuration, activeEffectDuration)
        const delay = effectDuration > 0 ? effectDuration + GAME_EFFECT_SETTLE_MS : 0
        if (delay > 0) kyoResultTimerRef.current = window.setTimeout(showKyoResult, delay)
        else showKyoResult()
      })
    }
    SignalR.on(CMD_GAME_PLAY, onGamePlay)

    /**
     * chat:relay (Cmd.HanChatRelay) — チャットメッセージ
     * レガシー: MJRoomWnd1.cpp:813-870 (CMJRoomWnd::ProcessCommonChatService)
    *   1. ヘッダ整形:  strHeader = pszPix + " : "
     *   2. 色決定 (スキン除く):
     *        self    : MAJAK_RGB_CHATSELF   = #003060
     *        player  : MAJAK_RGB_CHATOTHER  = #000000
     *        viewer  : MAJAK_RGB_CHATVIEWER = #606060
     *        abuse   : MAJAK_RGB_CHATABUSE  = #ff0000  (サーバから isAbuse フラグが来た場合)
     *      » 行全体を 1色で描画 (名前だけ別色 はレガシーにない)
    * フィールド: k3e, k41e / 互換: pix, string
     */
    const onChat = (data: Record<string, unknown>) => {
      if (!mounted) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      const myPix    = useAuthStore.getState().player?.pix ?? ''
      const isSelf   = pix !== '' && pix === myPix
      const isAbuse  = Boolean(data.isAbuse ?? data.abuse ?? false)
      // プレイヤー一覧にいるかどうかで player/viewer を判定
      // (レガシー MJRoomWnd1.cpp:822-828 m_tbl.GetMemInf(i) ループ相当)
      const isPlayer = playersRef.current.some(p => p.playerId === pix)
      const isUserViewer = !playersRef.current.some(p => p.playerId === myPix)
      const chatRules = chatRulesRef.current
      if (!isSelf && !chatRules.replayChannel) {
        if (!chatRules.chatEnabled) return
        if (!chatRules.viewerChatVisible && isUserViewer === isPlayer) return
      }
      let color: string
      if (isAbuse)         color = legacyPalette.chatAbuse
      else if (isSelf)     color = legacyPalette.chatSelf
      else if (isPlayer)   color = legacyPalette.chatOther
      else                 color = legacyPalette.chatViewer
      const senderName = String(data.mjkk34e ?? data.k8e ?? data.nickName ?? data.nickname ?? data.name ?? '')
      setChatLog(prev => appendRoomLog(prev,
        {
          id:   nextMessageId(),
          name: senderName || displayNameForPix(pix),
          pix,
          text: String(data.k41e ?? data.string ?? ''),
          color,
        },
      ))
      if (!isAbuse) playMajakChat()
    }
    SignalR.on('hc1e', onChat)

    const onUseEmoticon = (data: Record<string, unknown>) => {
      if (!mounted) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      const type = normalizeEmoticonType(data[KEY_EMOTICON_ID] ?? data.emoticonId ?? data.type)
      if (!pix || type == null) return
      const player = playersRef.current.find(p => p.playerId === pix)
      if (!player || player.pos < 0 || player.pos > 3) return
      const myPix = useAuthStore.getState().player?.pix ?? ''
      const me = playersRef.current.find(p => p.playerId === myPix)
      setActiveEmoticons(prev => [
        ...prev.filter(item => item.pix !== pix),
        { id: `${Date.now()}-${emoticonSeqRef.current++}`, pix, type, pos: seatToLegacyLoc(player.pos, me?.pos), startedAt: performance.now() },
      ])
    }
    SignalR.on(CMD_USE_EMOTICON, onUseEmoticon)

    /** InviteResponse — ProcessResponseInviteGameCommand: 招待応答結果 */
    const onInviteResponse = (data: Record<string, unknown>) => {
      if (!mounted) return
      const pix = String(data.k3e ?? data.pix ?? data['member' + 'Id'] ?? '')
      if (!pix || (pendingInviteTargetRef.current && pendingInviteTargetRef.current !== pix)) return
      pendingInviteTargetRef.current = null
      const yesNo = data.k64e
      const displayName = pendingInviteTargetNameRef.current || displayNameForPix(pix)
      pendingInviteTargetNameRef.current = ''
      if (data.rejectReason === 'inviteDisabled') {
        void showMessage(`${displayName}さんはゲーム招待を拒否しています。`, '招待できません')
      }
      const text = yesNo === 'v7e'
        ? `${displayName}さんがゲームへの招待を承諾しました。`
        : yesNo === 'v6e'
          ? `${displayName}さんから応答がありませんでした。`
          : `${displayName}さんがゲームへの招待を辞退しました。`
      const entry = { id: nextMessageId(), name: 'System', text, color: legacyPalette.notice }
      setChatLog(prev => appendRoomLog(prev, entry))
      setStatusLog(prev => appendRoomLog(prev, entry))
    }
    SignalR.on('c23e', onInviteResponse)

    /**
     * smmc1e (Cmd.SendOkButton) — 全プレイヤーの OK ボタン状態のブロードキャスト
     * レガシー: MJRoomWnd3.cpp:75-94 (DispatchServices "smmc1e")
     *   for(mem=0..MAX_PLAYER) GetVal("smmk2e{mem}", nPush)
     *     → m_nReadyFlag bitをセット/クリア
     * 各 座席位置の ready 状態を players リストに反映させる。
     */
    const onOkButtonState = (data: Record<string, unknown>) => {
      if (!mounted) return
      if (inlineGameActiveRef.current) return
      const readyByPos: Record<number, boolean> = {}
      for (let i = 0; i < 4; i++) {
        const v = data[`smmk2e${i}`]
        if (v != null) readyByPos[i] = Number(v) !== 0
      }
      console.info('[GameStartTiming] ready state received', {
        roomId,
        readyByPos,
        elapsedSinceReadyClickMs: readyClickedAtRef.current > 0 ? Math.round(performance.now() - readyClickedAtRef.current) : null,
      })
      setPlayers(prev => prev.map(p =>
        p.pos in readyByPos ? { ...p, ready: readyByPos[p.pos] } : p,
      ))
      // 自分の ready 状態も同期 (smmc2e 応答よりもブロードキャストが信頼できる)
      const myPix = useAuthStore.getState().player?.pix ?? ''
      const me = playersRef.current.find(p => p.playerId === myPix)
      if (me && me.pos in readyByPos) setIsReady(readyByPos[me.pos])
    }
    SignalR.on('smmc1e', onOkButtonState)

    /**
     * channel:notice (Cmd.Notice) — お知らせ表示
     * 応答フィールド: message
     */
    const onNotice = (data: Record<string, unknown>) => {
      if (!mounted) return
      setNotice(readNoticePayload(data))
    }
    SignalR.on('c40e', onNotice)

    /**
     * mjkc4e (Cmd.AutoStart) — 全員準備OK→ゲーム開始通知
     * レガシー ProcessCommand_AutoStart 相当
     * 応答フィールド: result, roomId, ...
     */
    const navigateToGame = () => {
      if (!mounted || gameNavigatedRef.current) return
      if (!gameLoadTimingRef.current.active) beginGameLoad('game-start')
      advanceGameLoad('resources', { trigger: 'navigateToGame' })
      gameNavigatedRef.current = true
      inlineGameEndedRef.current = false
      gameReportHandledRef.current = false
      setHanResData(null)
      setHanResFlags({ hasTor: false, hasTip: false, isViewer: false, isTournament: false })
      setLevelUp(null)
      setAnnounceData(null)
      setPendingAnnounces([])
      setIsReady(false)
      setPlayers(prev => prev.map(player => ({ ...player, ready: false })))
      logRejoinProbe('navigateToGame / set inlineGame true')
      if (DEBUG_GAME) console.info('[RoomScreen] inline game mount', {
        roomId,
      })
      inlineGameLoadingVisibleRef.current = true
      setInlineGameLoading(true)
      setInlineGame(true)
    }

    const onGameStart = (data: Record<string, unknown>) => {
      if (!mounted) return
      console.info('[GameStartTiming] AutoStart received', {
        roomId,
        elapsedSinceReadyClickMs: readyClickedAtRef.current > 0 ? Math.round(performance.now() - readyClickedAtRef.current) : null,
        receivedAt: new Date().toISOString(),
      })
      logRejoinProbe('mjkc4e game start response', { result: data.k1e ?? data.result, data })
      const result = data.k1e ?? data.result
      if (result != null && !isOk(result)) {
        showError('ゲーム開始に失敗しました')
        return
      }
      gemGameRef.current = asNumber(data[KEY_GEM_GAME] ?? data.gemGame, 0)
      putGemGameStatus(gemGameRef.current)
      gameReconnectActiveRef.current = shouldRequestInitialGameResync(gameReconnectActiveRef.current, true)
      navigateToGame()
    }
    SignalR.on('mjkc4e', onGameStart)

    const onViewRoom = (data: Record<string, unknown>) => {
      if (!mounted) return
      clearRoomActionPending('c18e response')
      logRejoinProbe('c18e view room response', {
        result: data.k1e ?? data.result,
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
        data,
      })
      const result = data.k1e ?? data.result
      if (result != null && !isOk(result)) {
        if (absorbAlreadyInRoom(data)) return
        handleRoomEntryFailure(data, '観戦に失敗しました')
        return
      }
      const nextRoomTitle = data.k45e ?? data.roomTitle
      if (typeof nextRoomTitle === 'string') setRoomTitle(nextRoomTitle)
      const nextRoomOption = data.k46e ?? data.roomOption
      if (typeof nextRoomOption === 'string') setCurrentRoomOption(nextRoomOption)
      navigateToGame()
    }
    SignalR.on('c18e', onViewRoom)

    const onAutoEnterRoom = (data: Record<string, unknown>) => {
      console.info('[GameReconnect] RoomScreen mjkc6e response received', {
        effectInstance,
        mounted,
        connectionId: SignalR.getConnection()?.connectionId,
        roomActionSentKey: roomActionSentKeyRef.current,
        result: data.k1e ?? data.result,
        state: data.state,
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
      })
      if (!mounted) {
        console.warn('[GameReconnect] RoomScreen mjkc6e response ignored: effect unmounted', {
          effectInstance,
          connectionId: SignalR.getConnection()?.connectionId,
        })
        return
      }
      clearRoomActionPending('mjkc6e response')
      logRejoinProbe('mjkc6e auto enter response', {
        result: data.k1e ?? data.result,
        responseRoomId: data.k42e ?? data.roomId,
        message: data.k2e ?? data.message,
        data,
      })
      const result = data.k1e ?? data.result
      if (result != null && !isOk(result)) {
        if (absorbAlreadyInRoom(data)) return
        handleRoomEntryFailure(data, 'ルームへの入室に失敗しました')
        return
      }
      const nextRoomTitle = data.k45e ?? data.roomTitle
      if (typeof nextRoomTitle === 'string') setRoomTitle(nextRoomTitle)
      const nextRoomOption = data.k46e ?? data.roomOption
      if (typeof nextRoomOption === 'string') setCurrentRoomOption(nextRoomOption)
      // Tournament reservation recovery can enter a waiting room. Only a
      // server-confirmed playing room has resync packets to reconstruct.
      if (gameReconnectActiveRef.current && Number(data.state ?? -1) === 2) navigateToGame()
      else if (Number(data.state ?? -1) !== 2) finishGameLoad({ reason: 'waiting-room-ready' })
    }
    SignalR.on('mjkc6e', onAutoEnterRoom)

    /** mjkc19e — CMJRoomWnd::ShowSlideAnnounce(ANNOUNCE_GET_TRICKTITLE/MAJAKTITLE) */
    const onGetTitle = (data: Record<string, unknown>) => {
      if (!mounted) return
      const count = asNumber(data[KEY_COUNT], 0)
      const announcements: SlideAnnounceData[] = []
      for (let index = 0; index < count; index++) {
        const type = asNumber(data[`${KEY_TITLE_TYPE}${index}`], -1)
        const code = asNumber(data[`${KEY_TITLE_CODE}${index}`], 0)
        if (type !== 0 && type !== 1) continue
        announcements.push({
          type: type as 0 | 1,
          code,
          name: String(data[`${KEY_TITLE_NAME}${index}`] ?? ''),
        })
      }
      if (announcements.length > 0) setPendingAnnounces(previous => [...previous, ...announcements])
    }
    SignalR.on('mjkc19e', onGetTitle)

    /** mjkc22e — CMJRoomWnd::ShowSlideAnnounce(ANNOUNCE_GET_RYUTAMA) + PutStatus */
    const onGetGem = (data: Record<string, unknown>) => {
      if (!mounted) return
      const count = asNumber(data[KEY_COUNT], 0)
      setPendingAnnounces(previous => [...previous, { type: 2, code: count, name: '' }])
      setStatusLog(prev => appendRoomLog(prev, { id: nextMessageId(), name: '', text: `龍珠を${count}個獲得しました。`, color: legacyPalette.normal, bold: true }))
    }
    SignalR.on('mjkc22e', onGetGem)

    const onYakumanBonus = (data: Record<string, unknown>) => {
      if (!mounted) return
      const pix = String(data.k3e ?? data.pix ?? '')
      setPendingAnnounces(previous => [...previous, {
        type: 3,
        code: 0,
        name: displayNameForPix(pix),
        name2: String(data[KEY_YAKU_NAME] ?? data.yakuName ?? ''),
      }])
    }
    SignalR.on(CMD_YAKUMAN_BONUS, onYakumanBonus)

    /**
     * smmc2e (Cmd.PushOkButton) — OKボタンプッシュ応答
     * レガシー: MJRoomWnd3.cpp:98-118 (DispatchServices "smmc2e")
     * 応答フィールド: smmk3e (lack money). result は送られない。
     */
    const onOkResult = (data: Record<string, unknown>) => {
      if (!mounted) return
      const lackMoney = Number(data.smmk3e ?? data.lackMoney ?? 0)
      if (lackMoney > 0) {
        showError(String(data.message ?? 'GPが不足しています。'))
        void exitRoomToLobby(me?.pos)
      }
    }
    SignalR.on('smmc2e', onOkResult)

    /**
    * c8e — CreateRoom コマンドの応答
     * AP-04 §8: ルーム作成成功時に受信する。
     * 応答フィールド: result(1=成功), roomId
     */
    const onRoomCreated = (data: Record<string, unknown>) => {
      if (!mounted) return
      clearRoomActionPending('c8e response')
      const isSuccess = Number(data.result) === 1 || data.k1e === 'v1e'
      const createdRoomId = Number(data.k42e ?? data.roomId ?? 0)
      if (!isSuccess || createdRoomId <= 0) {
        if (absorbAlreadyInRoom(data)) return
        showError(String(data.k2e ?? data.message ?? 'ルームの作成に失敗しました'))
        navigate(`/channel/${channelId ?? ''}/lobby`)
        return
      }
      // URL を /room/{realRoomId} に革新 (履歴を置換)
      navigate(
        `/channel/${channelId}/lobby/room/${createdRoomId}`,
        {
          replace: true,
          state: {
            serverUrl,
            mode: 'enter',
            skipEnterChannel: true,
            roomTitle,
            roomOption: currentRoomOption,
            customBgId: customBoardId,
            customHaiId,
            customBoardType,
          },
        },
      )
      void SignalR.send('c16e', {}).catch(() => {})
    }
    SignalR.on('c8e', onRoomCreated)

    /**
     * レガシー CMJRoomWnd::OnSocketClose:
     * CHgGameWnd::OnSocketClose がエラーを返した場合は ForceExit() する。
     */
    let connectionLostHandled = false
    const onConnectionLost = (error?: Error) => {
      if (!mounted || connectionLostHandled) return
      connectionLostHandled = true
      logRejoinProbe('SignalR connection lost', { errorMessage: error?.message ?? String(error ?? '') })
      console.error('[RoomScreen] SignalR connection lost', {
        channelId,
        roomId,
        pix: useAuthStore.getState().player?.pix ?? '',
        error,
      })
      navigate('/channel', { replace: true })
    }
    SignalR.onConnectionLost(onConnectionLost)
    const onBrowserOffline = () => onConnectionLost()
    window.addEventListener('offline', onBrowserOffline)

    async function setup(forceRejoin = false) {
      const setupAttempt = `${effectInstance}:${forceRejoin ? 'rejoin' : 'initial'}`
      const player = useAuthStore.getState().player
      // AP-04 §8: ルーム入室時に初めて WebSocket 接続
      const hubUrl = serverUrl ? `${serverUrl}/hubs/majak` : '/hubs/majak'
      const navigationType = getDocumentNavigationType()
      const reconnectTrigger = forceRejoin ? 'signalr' : navigationType === 'reload' ? 'page-reload' : locState.resumePlaying ? 'continuation' : ''
      if (reconnectTrigger) gameReconnectActiveRef.current = true
      beginGameLoad(gameReconnectActiveRef.current ? 'game-reconnect' : 'room-entry', reconnectTrigger)
      console.info('[GameReconnect] RoomScreen setup start', {
        setupAttempt,
        mounted,
        forceRejoin,
        reconnectTrigger,
        gameReconnect: gameReconnectActiveRef.current,
        channelId,
        roomId,
        mode: locState.mode ?? 'enter',
        skipEnterChannel: Boolean(locState.skipEnterChannel),
        roomActionSentKey: roomActionSentKeyRef.current,
        hubUrl,
        connectionIdBeforeConnect: SignalR.getConnection()?.connectionId,
        connectionStateBeforeConnect: SignalR.getConnection()?.state ?? 'missing',
      })
      logRejoinProbe('setup start', { forceRejoin, gameReconnect: gameReconnectActiveRef.current, reconnectTrigger, hubUrl, skipEnterChannel: Boolean(locState.skipEnterChannel) })
      // AP-04 §8: ルーム入室時の WebSocket 接続
      // ロビーで同一サーバーに接続済みなら再接続しない (signalr.ts で自動スキップ)
      // 異なるサーバー URL の場合 (マルチサーバー構成) は再接続する
      await SignalR.connect(hubUrl)
      console.info('[GameReconnect] RoomScreen SignalR.connect resolved', {
        setupAttempt,
        mounted,
        connectionId: SignalR.getConnection()?.connectionId,
        connectionState: SignalR.getConnection()?.state ?? 'missing',
        roomActionSentKey: roomActionSentKeyRef.current,
      })
      if (!mounted) {
        console.warn('[GameReconnect] RoomScreen setup stopped after connect: effect unmounted', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
        })
        return
      }
      advanceGameLoad('channel', { hubUrl })
      logRejoinProbe('SignalR connected in setup', { forceRejoin, hubUrl })
      if (gameReconnectActiveRef.current || forceRejoin || !locState.skipEnterChannel) {
        const isContinue = gameReconnectActiveRef.current
        logRejoinProbe('send c1e enter channel', {
          forceRejoin,
          isContinue,
          ignoredSkipEnterChannel: gameReconnectActiveRef.current && Boolean(locState.skipEnterChannel),
        })
        await enterChannel(buildEnterChannelPayload(channelId ?? '', player, isContinue))
        console.info('[GameReconnect] RoomScreen c1e request completed', {
          setupAttempt,
          mounted,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionSentKey: roomActionSentKeyRef.current,
        })
      }
      if (!mounted) {
        console.warn('[GameReconnect] RoomScreen setup stopped after c1e: effect unmounted', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
        })
        return
      }
      advanceGameLoad('room', { skipEnterChannel: Boolean(locState.skipEnterChannel) })
      const roomActionKey = `${channelId ?? ''}:${roomId ?? ''}:${locState.mode ?? 'enter'}:${serverUrl}`
      console.info('[GameReconnect] RoomScreen evaluating room action', {
        setupAttempt,
        connectionId: SignalR.getConnection()?.connectionId,
        forceRejoin,
        mode: locState.mode ?? 'enter',
        roomActionKey,
        previousRoomActionSentKey: roomActionSentKeyRef.current,
        keysMatch: roomActionSentKeyRef.current === roomActionKey,
        gameReconnect: gameReconnectActiveRef.current,
        inlineGame: inlineGameActiveRef.current,
        locationKey: location.key,
      })
      if (!forceRejoin && roomActionSentKeyRef.current === roomActionKey) {
        const navigationType = getDocumentNavigationType()
        const shouldReturnToLobby = !gameReconnectActiveRef.current && !inlineGameActiveRef.current && location.key === 'default'
        logRejoinProbe('skip room action: already sent key', { roomActionKey, navigationType, locationKey: location.key, shouldReturnToLobby })
        console.warn('[GameReconnect] RoomScreen room-entry command skipped: action key already sent', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionKey,
          navigationType,
          locationKey: location.key,
          gameReconnect: gameReconnectActiveRef.current,
          inlineGame: inlineGameActiveRef.current,
          shouldReturnToLobby,
        })
        if (shouldReturnToLobby) {
          navigate(channelId ? `/channel/${channelId}` : '/channel', { replace: true })
        }
        return
      }
      roomActionSentKeyRef.current = roomActionKey
      console.info('[GameReconnect] RoomScreen room action key claimed', {
        setupAttempt,
        connectionId: SignalR.getConnection()?.connectionId,
        roomActionKey,
        mode: locState.mode ?? 'enter',
      })
      if (createMode) {
        logRejoinProbe('send c8e create room', { roomActionKey })
        startRoomActionPending('c8e', { roomActionKey })
        await SignalR.send('c8e', buildCreateRoomPayload(channelId ?? '', player, locState))
      } else if (locState.mode === 'auto') {
        const autoEnterPayload = {
          ...(locState.autoEnterPayload ?? {}),
          pix: player?.pix ?? '',
          k3e: player?.pix ?? '',
        }
        const sendAutoEnter = () => SignalR.send('mjkc6e', autoEnterPayload)
        const autoEnterLogPayload = autoEnterPayload as Record<string, unknown>
        console.info('[GameReconnect] RoomScreen mjkc6e send start', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionKey,
          requestedRoomId: autoEnterLogPayload.roomId ?? autoEnterLogPayload.k42e ?? roomId,
        })
        logRejoinProbe('send mjkc6e auto enter', { roomActionKey, payload: autoEnterPayload })
        startRoomActionPending('mjkc6e', { roomActionKey })
        await sendAutoEnter()
        console.info('[GameReconnect] RoomScreen mjkc6e invoke resolved', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionKey,
        })
      } else if (locState.mode === 'view') {
        const numericRoomId = Number(roomId ?? 0)
        const viewRoomPayload = {
          roomId: numericRoomId,
          k42e: numericRoomId,
          roomPwd: locState.roomPassword ?? '',
          roomPassword: locState.roomPassword ?? '',
          k67e: locState.roomPassword ?? '',
          playerType: 'v5e',
          k57e: 'v5e',
          ...(locState.tournamentViewPayload ?? {}),
        }
        const sendViewRoom = () => SignalR.send('c18e', viewRoomPayload)
        logRejoinProbe('send c18e view room', { roomActionKey, numericRoomId })
        startRoomActionPending('c18e', { roomActionKey, numericRoomId })
        await sendViewRoom()
        await SignalR.send('c16e', {})
      } else {
        // 既存ルーム入室モード: c14e (G::commandEnterRoom)
        const numericRoomId = Number(roomId ?? 0)
        const enterRoomPayload = {
          roomId: numericRoomId,
          k42e: numericRoomId,
          roomPassword: locState.roomPassword ?? '',
          k67e: locState.roomPassword ?? '',
        }
        const sendEnterRoom = () => SignalR.send('c14e', enterRoomPayload)
        console.info('[GameReconnect] RoomScreen c14e send start', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionKey,
          numericRoomId,
        })
        logRejoinProbe('send c14e enter room', { roomActionKey, numericRoomId })
        startRoomActionPending('c14e', { roomActionKey, numericRoomId })
        await sendEnterRoom()
        console.info('[GameReconnect] RoomScreen c14e invoke resolved', {
          setupAttempt,
          connectionId: SignalR.getConnection()?.connectionId,
          roomActionKey,
          numericRoomId,
        })
      }
    }
    const onReconnected = () => {
      if (!mounted || connectionLostHandled) return
      logRejoinProbe('SignalR reconnected callback')
      gameReconnectActiveRef.current = true
      roomActionSentKeyRef.current = ''
      setup(true).catch(err => {
        if (!mounted) return
        logRejoinProbe('reconnect setup failed', { errorMessage: err instanceof Error ? err.message : String(err) })
        console.error('[RoomScreen] reconnect setup failed', err)
        handleRoomEntryFailure({ message: 'ルームへの再接続に失敗しました' })
      })
    }
    SignalR.onReconnected(onReconnected)
    setup().catch(err => {
      if (!mounted) return
      logRejoinProbe('initial setup failed', { errorMessage: err instanceof Error ? err.message : String(err) })
      console.error('[RoomScreen] setup failed', err)
      handleRoomEntryFailure({ message: 'ルームへの接続に失敗しました' })
    })

    return () => {
      mounted = false
      if (kyoResultTimerRef.current !== null) window.clearTimeout(kyoResultTimerRef.current)
      kyoResultTimerRef.current = null
      console.info('[GameReconnect] RoomScreen connection effect cleanup', {
        effectInstance,
        channelId,
        roomId,
        mode: locState.mode ?? 'enter',
        roomActionSentKey: roomActionSentKeyRef.current,
        connectionId: SignalR.getConnection()?.connectionId,
        connectionState: SignalR.getConnection()?.state ?? 'missing',
      })
      if (pendingChannelEntryHandler) {
        SignalR.off('c1e', pendingChannelEntryHandler)
        pendingChannelEntryHandler = null
      }
      SignalR.offConnectionLost(onConnectionLost)
      SignalR.offReconnected(onReconnected)
      window.removeEventListener('offline', onBrowserOffline)
      SignalR.off('c14e',             onRoomEnter)
      SignalR.off('c55e',             onReserveChance)
      SignalR.off('c34e',             onRoomEnterError)
      SignalR.off('c16e',             onPlayerList)
      SignalR.off('c7e',              onChannelMemberList)
      SignalR.off('c5e',              onMemberJoined)
      SignalR.off('c6e',              onMemberLeft)
      SignalR.off('mjkroom',             onRoomState)
      SignalR.off('c32e',             onGameReport)
      SignalR.off(CMD_GAME_PLAY,      onGamePlay)
      SignalR.off('mjkc5e',              onAutoExitRoom)
      SignalR.off('hc1e',            onChat)
      SignalR.off(CMD_USE_EMOTICON,  onUseEmoticon)
      SignalR.off('c23e',            onInviteResponse)
      SignalR.off('smmc1e',           onOkButtonState)
      SignalR.off('c40e',            onNotice)
      SignalR.off('mjkc4e',           onGameStart)
      SignalR.off('c18e',             onViewRoom)
      SignalR.off('mjkc6e',           onAutoEnterRoom)
      SignalR.off('mjkc19e',          onGetTitle)
      SignalR.off('mjkc22e',          onGetGem)
      SignalR.off(CMD_YAKUMAN_BONUS,  onYakumanBonus)
      SignalR.off('smmc2e',           onOkResult)
      SignalR.off('c8e',             onRoomCreated)
      // WebSocket は LobbyScreen が管理する (同一サーバーなら切断しない)
      // 別サーバー URL の場合でも LobbyScreen 側で再接続される
    }
  }, [channelId, roomId, navigate, serverUrl, createMode])

  /** チャットログ自動スクロール */
  useEffect(() => {
    if (chatLogRef.current)   chatLogRef.current.scrollTop   = chatLogRef.current.scrollHeight
    if (statusLogRef.current) statusLogRef.current.scrollTop = statusLogRef.current.scrollHeight
  }, [chatLog, statusLog])

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(null), notice.durationMs)
    return () => window.clearTimeout(timer)
  }, [notice])

  const myPix = useAuthStore.getState().player?.pix ?? ''
  const accuseChatMessages = chatLog.filter(msg => msg.name && msg.name !== 'System' && msg.text)
  const accuseChatContent = accuseChatMessages.map(msg => `[${msg.name}] ${msg.text}`).join('\n')
  const accuseSpeakers = [...new Set(accuseChatMessages.map(msg => msg.pix ?? msg.name).filter(pix => pix !== myPix))]
  const accuseSpeakerNameById = new Map(accuseSpeakers.map(pix => [pix, displayNameForPix(pix)]))
  const accuseSpeakerKey = accuseSpeakers.join('\0')

  useEffect(() => {
    const onAccuse = () => {
      if (accuseSpeakers.length === 0 || accuseChatContent.length === 0) {
        void showMessage('メッセージがありません。', 'お知らせ')
        return
      }
      const now = window.performance.now()
      if (now - lastAccuseOpenedAtRef.current < 30000) return
      lastAccuseOpenedAtRef.current = now
      setShowAccuse(true)
    }
    window.addEventListener(MAJAK_ACCUSE_EVENT, onAccuse)
    return () => window.removeEventListener(MAJAK_ACCUSE_EVENT, onAccuse)
  }, [accuseChatContent, accuseSpeakerKey])

  const chatInputDisabled = !chatEnabled

  /** チャット送信 (Enter キー → HG_UWM_IMGCHATSENDSTRING 相当) */
  const sendChat = async () => {
    if (chatInputDisabled || !chatText.trim()) return
    // chat:relay = Cmd.HanChatRelay; G::keyString(k41e) / Web 互換 string
    try {
      await SignalR.send('hc1e', { k41e: chatText, string: chatText })
      setChatText('')
    } catch (err) {
      console.error('[RoomScreen] chat send failed', err)
      showError('チャット送信に失敗しました')
    }
  }
  const onChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') sendChat()
    /** ESC → HG_UWM_IMGCHATSENDESCKEY 相当: 入力欄クリア */
    if (e.key === 'Escape') setChatText('')
  }

  useEffect(() => {
    const focusChat = () => {
      if (!chatInputDisabled) chatInputRef.current?.focus()
    }
    window.addEventListener(GAME_FOCUS_CHAT_EVENT, focusChat)
    return () => window.removeEventListener(GAME_FOCUS_CHAT_EVENT, focusChat)
  }, [chatInputDisabled])

  useEffect(() => {
    if (activeEmoticons.length === 0) return
    let frame = 0
    const tick = () => {
      const now = performance.now()
      setEmoticonNow(now)
      setActiveEmoticons(prev => prev.filter(item => {
        const duration = (EMOTICON_STEPS[item.type]?.length ?? EMOTICON_STEPS[0].length) * EMOTICON_FRAME_MS
        return now - item.startedAt <= duration
      }))
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [activeEmoticons.length])

  /** 準備完了トグル (CMJRoomWnd::SendRdyPlay 相当)
   * レガシー: MJRoomWnd3.cpp:483-489
   *   CHgParser mp(G::serviceRoom, "smmc2e");
   *   mp.AddValue( G::keyDummy, 0 );
   *   SendDataEncrypt(mp);
   * サーバーは seat 位置を見てトグルし、smmc1e で全員の状態をブロードキャストするため、
   * クライアントからは "ready" 値を送らず、ready 状態の更新は smmc1e 受信に任せる。
   * (ライトフィードバックのため setIsReady は仮更新を保持)
   */
  const onToggleReady = async () => {
    if (!hasPlayerSeat) return
    if (autoMatchingChannel) return
    if (trainingChannel && Boolean(me?.isHost) && !trainingReadyToStart) return
    if (!(trainingChannel && me?.isHost)) setIsReady(prev => !prev)
    readyClickedAtRef.current = performance.now()
    console.info('[GameStartTiming] ready button clicked', { roomId, clickedAt: new Date().toISOString() })
    // Cmd.PushOkButton = "smmc2e" (C→S 送信 / S→C 応答)
    await SignalR.send('smmc2e', { dummy: 0 })
    console.info('[GameStartTiming] ready command sent', {
      roomId,
      elapsedSinceReadyClickMs: Math.round(performance.now() - readyClickedAtRef.current),
    })
  }

  /** 招待 (ID_GAMEINVI 相当)
   * 原典: CHgChannelWnd::SendInviteGameToMember / Cmd_Channel_InviteGameToMemberToS
   * フィールド: targetPix (parser target), k3e, k42e, k65e, k64e
   */
  const onGameInvi = async (targetPix: string) => {
    setShowInviteList(false)
    pendingInviteTargetRef.current = targetPix
    pendingInviteTargetNameRef.current = channelMembersRef.current.find(member => member.pix === targetPix)?.name ?? ''
    const pix = useAuthStore.getState().player?.pix ?? ''
    await SignalR.send('c22e', {
      k3e: pix,
      targetMemberNo: targetPix,
      targetPix,
      k42e: roomId ?? '',
      k65e: '一緒にプレイしませんか？',
      k64e: false,
    })
  }

  const openInviteList = () => {
    if (autoMatchingChannel) return
    setShowInviteList(true)
    void SignalR.send('c7e', buildGetMemberListPayload(channelId ?? '')).catch(() => {})
  }

  const openChannelMemberInfo = (targetPix: string) => {
    const member = channelMembers.find(item => item.pix === targetPix)
    if (!member) return
    setSelectedViewer({
      pix: member.pix,
      name: member.name,
      avatarId: member.avatarId,
      sex: member.sex === 'F' || member.sex === 'female' ? 'female' : 'male',
    })
  }

  const myPixForSeat = useAuthStore.getState().player?.pix ?? ''
  const me = players.find(p => p.playerId === myPixForSeat)
  const routeExpectsPlayerSeat = locState.mode === 'create' || locState.mode === 'enter' || locState.mode === 'auto' || Boolean(locState.resumePlaying)
  const membershipResolved = players.length > 0 || viewers.length > 0
  const isViewerUser = locState.mode === 'view' || (!routeExpectsPlayerSeat && membershipResolved && !me)
  const hasPlayerSeat = Boolean(me) && !isViewerUser
  // Inline mobile play can begin before c16e refreshes the local seat record.
  const hasInlineGamePlayerControls = inlineGame && !isViewerUser && (hasPlayerSeat || routeExpectsPlayerSeat)
  const emoticonButtonDisabled = !hasPlayerSeat || chatInputDisabled
  const sendEmoticon = async (type: number) => {
    if (emoticonButtonDisabled || type < 0 || type >= EMOTICON_COUNT) return
    try {
      await SignalR.send(CMD_USE_EMOTICON, {
        [KEY_EMOTICON_ID]: type,
        [KEY_EMOTICON_AVATAR_ID]: useAuthStore.getState().player?.avatarId ?? '',
      })
    } catch (err) {
      console.error('[RoomScreen] emoticon send failed', err)
      showError('表情送信に失敗しました')
    }
  }
  const isRoomFull = players.length >= 4
  const skillChannel = extractSubId(channelId)[2] === 'R'
  const allReady = players.length > 0 && players.every(p => p.ready)
  const nonHostPlayers = players.filter(p => !p.isHost)
  const trainingReadyToStart = nonHostPlayers.every(p => p.ready)
  const effectiveRoomFull = trainingChannel || isRoomFull
  const effectiveAllReady = trainingChannel ? trainingReadyToStart : allReady
  const autoControlEnabled = hasInlineGamePlayerControls
  const childAutoControlEnabled = autoControlEnabled && !autoControl.prox
  const subId = extractSubId(channelId)
  const chanceButtonVisible = hasChanceItem
  const chanceButtonSendAllowed = hasPlayerSeat && !(subId[1] === '1' && subId[2] === 'D')
  const mobileIngameToolDrawerStyle = {
    '--majak-mobile-ingame-tool-top': `${Math.round(MOBILE_TOP_RIGHT_HUD_PANEL_BOTTOM * mobileIngameScale + MOBILE_INGAME_TOOL_HUD_GAP)}px`,
  } as CSSProperties
  const closeHanRes = () => {
    setHanResData(null)
    if (hanResFlags.isViewer) {
      void exitRoomToLobby(undefined, true)
      return
    }
    finishInlineGame('han result closed')
  }
  const dispatchAutoControl = (state: AutoControlState) => {
    window.dispatchEvent(new CustomEvent(GAME_AUTO_CONTROL_EVENT, { detail: state }))
  }
  const updateAutoControl = (updater: (prev: AutoControlState) => AutoControlState) => {
    setAutoControl(prev => {
      const next = updater(prev)
      autoControlRef.current = next
      dispatchAutoControl(next)
      return next
    })
  }
  useEffect(() => {
    const onKyokuStarted = () => updateAutoControl(prev => resetAutoControlForNewKyoku(
      prev,
      shouldEnableAutoPassAtKyokuStart(roomCfg.nChkPAS, currentRoomOption),
    ))
    window.addEventListener(GAME_KYOKU_STARTED_EVENT, onKyokuStarted)
    return () => window.removeEventListener(GAME_KYOKU_STARTED_EVENT, onKyokuStarted)
  }, [currentRoomOption, roomCfg.nChkPAS])
  useEffect(() => {
    const onLocalReach = () => updateAutoControl(prev => enableAutoTapAfterReach(prev, roomCfg.bChkAUT))
    window.addEventListener(GAME_LOCAL_REACH_EVENT, onLocalReach)
    return () => window.removeEventListener(GAME_LOCAL_REACH_EVENT, onLocalReach)
  }, [roomCfg.bChkAUT])
  const onSetProx = () => updateAutoControl(prev => {
    const prox = !prev.prox
    return { prox, autoTap: prox, autoPass: prox, autoHora: prox }
  })
  const onSetAuto = () => updateAutoControl(prev => ({ ...prev, autoTap: !prev.autoTap }))
  const onSetPass = () => updateAutoControl(prev => ({ ...prev, autoPass: !prev.autoPass }))
  const onSetHora = () => updateAutoControl(prev => ({ ...prev, autoHora: !prev.autoHora }))
  const onViewerRotate = (delta: 1 | 3) => {
    window.dispatchEvent(new CustomEvent(PAIFU_ROTATE_EVENT, { detail: { delta } }))
  }
  const onViewerHandToggle = () => {
    setViewerHandHidden(prev => {
      const next = !prev
      window.dispatchEvent(new CustomEvent(PAIFU_HAND_OPEN_EVENT, { detail: { open: !next } }))
      return next
    })
  }
  useEffect(() => {
    const onKyoResultAction = (event: Event) => {
      if (inlineGameEndedRef.current) return
      if (isViewerUser) return
      const detail = (event as CustomEvent<{ roomId?: string; seatOrder?: number; actionSeq?: number; localDeadlineAt?: number }>).detail ?? {}
      setKyoResultAction({
        roomId: String(detail.roomId ?? roomId ?? ''),
        seatOrder: Number(detail.seatOrder ?? me?.pos ?? 0),
        actionSeq: Number(detail.actionSeq ?? 0) || undefined,
        localDeadlineAt: Number(detail.localDeadlineAt ?? 0) || undefined,
      })
    }
    window.addEventListener(KYO_RESULT_ACTION_EVENT, onKyoResultAction)
    const onKyoResultProgress = (event: Event) => {
      const detail = (event as CustomEvent<{ seatOrder?: number; durationMs?: number; localDeadlineAt?: number }>).detail ?? {}
      const seatOrder = Number(detail.seatOrder)
      const durationMs = Math.max(0, Number(detail.durationMs ?? 0))
      const localDeadlineAt = Number(detail.localDeadlineAt ?? 0)
      if (!Number.isInteger(seatOrder) || seatOrder < 0 || !Number.isFinite(localDeadlineAt) || localDeadlineAt <= 0) return
      setKyoResultProgress(current => {
        const previous = current[seatOrder]
        return {
          ...current,
          [seatOrder]: {
            durationMs: previous?.durationMs || durationMs,
            localDeadlineAt: previous ? Math.min(previous.localDeadlineAt, localDeadlineAt) : localDeadlineAt,
            submitted: previous?.submitted ?? false,
          },
        }
      })
    }
    const onKyoResultConfirmed = (event: Event) => {
      const seatOrder = Number((event as CustomEvent<{ seatOrder?: number }>).detail?.seatOrder)
      if (!Number.isInteger(seatOrder) || seatOrder < 0) return
      setKyoResultProgress(current => {
        const progress = current[seatOrder]
        return {
          ...current,
          [seatOrder]: progress
            ? { ...progress, submitted: true }
            : { durationMs: 0, localDeadlineAt: 0, submitted: true },
        }
      })
    }
    window.addEventListener(KYO_RESULT_PROGRESS_EVENT, onKyoResultProgress)
    window.addEventListener(KYO_RESULT_CONFIRMED_EVENT, onKyoResultConfirmed)
    return () => {
      window.removeEventListener(KYO_RESULT_ACTION_EVENT, onKyoResultAction)
      window.removeEventListener(KYO_RESULT_PROGRESS_EVENT, onKyoResultProgress)
      window.removeEventListener(KYO_RESULT_CONFIRMED_EVENT, onKyoResultConfirmed)
    }
  }, [isViewerUser, me?.pos, roomId])
  const sendKyoResultAction = async () => {
    const request = kyoResultAction
    setKyoResultAction(null)
    if (!request) return
    if (request.localDeadlineAt !== undefined && performance.now() >= request.localDeadlineAt) return
    setKyoResultSubmitted(true)
    setKyoResultProgress(current => {
      const progress = current[request.seatOrder]
      return progress ? { ...current, [request.seatOrder]: { ...progress, submitted: true } } : current
    })
    await SignalR.send(CMD_GAME_PLAY, {
      playType: 'MJPID_ACTION',
      roomId: request.roomId,
      seatOrder: request.seatOrder,
      action: ACT_PAS,
      bipaiIndex: [],
      actionSeq: request.actionSeq,
    }).catch(() => {})
  }
  useEffect(() => {
    if (!autoControl.prox || !kyoResData || !kyoResultAction) return
    const timer = window.setTimeout(() => { void sendKyoResultAction() }, 3000)
    return () => window.clearTimeout(timer)
  }, [autoControl.prox, kyoResData, kyoResultAction])
  const onReserveChance = async () => {
    if (!chanceButtonSendAllowed || !hasChanceItem) return
    const reserveChance = !chanceReserved
    setChanceReserved(reserveChance)
    try {
      await SignalR.send('c55e', { k118e: reserveChance, reserveChance })
    } catch (err) {
      setChanceReserved(!reserveChance)
      console.error('[RoomScreen] reserve chance failed', err)
    }
  }
  const promptImage = autoMatchingChannel || !me
    ? null
    : trainingChannel && me.isHost
      ? trainingReadyToStart ? promptSrc.pushStart : promptSrc.waitReady
    : !effectiveRoomFull
      ? promptSrc.waitEntry
      : !me.ready
        ? promptSrc.pushReady
        : !effectiveAllReady
          ? promptSrc.waitReady
          : promptSrc.waitStart
  const responsivePromptText = autoMatchingChannel || !me
    ? null
    : trainingChannel && me.isHost
      ? trainingReadyToStart ? '対局を開始できます。開始を押してください。' : '参加者の準備が完了するまでお待ちください。'
    : !effectiveRoomFull
      ? '参加者を待っています。'
      : !me.ready
        ? '準備完了を押してください。'
        : !effectiveAllReady
          ? 'ほかの参加者の準備を待っています。'
          : '全員の準備が完了しました。開始を待っています。'
  const readyButtonImage = trainingChannel && me?.isHost
    ? bgSkinSrc('mj_btStart')
    : bgSkinSrc('mj_btOK', 'mj_btOk')
  const readyButtonFallbackImage = trainingChannel && me?.isHost
    ? bgSkinFallbackSrc('mj_btStart')
    : bgSkinFallbackSrc('mj_btOK', 'mj_btOk')
  const readyButtonDisabled = trainingChannel && me?.isHost
    ? !trainingReadyToStart
    : autoMatchingChannel
  const optionString = currentRoomOption
  const optionIcons = [
    optionSprite(OPTION_ICON.set, readOptionDigit(optionString, 0, 1), 948, 8, 1),
    optionSprite(OPTION_ICON.kui, readOptionDigit(optionString, 3, 0), 965, 8, 1),
    optionSprite(OPTION_ICON.uma, readOptionDigit(optionString, 1, 2), 982, 8, 3),
    optionSprite(OPTION_ICON.ron, readOptionDigit(optionString, 12, 0), 999, 8, 2),
    optionSprite(OPTION_ICON.red, readOptionDigit(optionString, 5, 2), 948, 25, 2),
    optionSprite(OPTION_ICON.spd, readOptionDigit(optionString, 2, 2), 965, 25, 3),
    optionSprite(OPTION_ICON.opn, readOptionDigit(optionString, 6, 0), 982, 25, 1),
    optionSprite(readOptionDigit(optionString, 14, 0) ? OPTION_ICON.cht : OPTION_ICON.ach, readOptionDigit(optionString, 14, 0) ? readOptionDigit(optionString, 7, 0) : 0, 999, 25, 1),
  ]

  useEffect(() => {
    if (!inlineGame || emoticonButtonDisabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      const match = /^F([1-6])$/.exec(event.key)
      if (!match) return
      event.preventDefault()
      void sendEmoticon(Number(match[1]) - 1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [emoticonButtonDisabled, inlineGame])

  if (roomActionPending && !inlineGame) {
    const pendingStage = (
      <div className="majak-room-entry-loading majak-screen-surface">
        <GameReconnectLoading visible currentStep={gameLoadStep} complete={gameLoadComplete} />
      </div>
    )
    if (isMobileIngame) {
      return (
        <div ref={mobileIngameShellRef} className="majak-mobile-ingame-shell majak-mobile-room-waiting-shell">
          <GameReconnectLoading visible currentStep={gameLoadStep} complete={gameLoadComplete} />
        </div>
      )
    }
    return pendingStage
  }

  if (useResponsiveWaitingLayout) {
    const mobileSeats = ([0, 1, 2, 3] as const).map(loc => ({
      loc,
      player: players.find(player => seatToLegacyLoc(player.pos, me?.pos) === loc),
    }))
    const readyLabel = autoMatchingChannel
      ? '待機中'
      : trainingChannel && me?.isHost
        ? '開始'
        : isReady ? '準備取消' : '準備完了'
    const readyButtonAttention = readyLabel === '準備完了' && !readyButtonDisabled

    return (
      <div className={`majak-mobile-room-waiting-screen${layoutMode === 'desktop' ? ' majak-responsive-desktop-room-waiting' : ''}${tengokuBoardSkin ? ' is-tengoku-skin' : ''}`}>
        <section className="majak-mobile-room-table">
          <div className="majak-mobile-room-table__header">
            <div>
              <div className="majak-mobile-eyebrow">対局待機</div>
              <h1>{roomTitle || `${roomId ?? ''}番部屋`}</h1>
            </div>
            <div className="majak-mobile-room-table__meta">
              <span>{players.length}/4</span>
              {viewers.length > 0 && <span>観戦 {viewers.length}</span>}
            </div>
          </div>

          <div className="majak-mobile-room-seats">
            {mobileSeats.map(({ loc, player }) => {
              const avatarFallback = getDefaultAvatarUrl(player?.sex === 'F' || player?.sex === 'female' ? 'female' : 'male')
              const levelText = player
                ? skillChannel ? `@ ${Number(player.skillCnt ?? 0)}局` : player.slevel || '庶民'
                : '空席'
              const ratingText = player && !skillChannel && player.rating > 0 && Number(player.nlevel ?? 0) < 100
                ? `[${String(player.rating).padStart(4, ' ')}]`
                : ''
              return (
                <article key={loc} className={`majak-mobile-room-seat majak-mobile-room-seat--${loc}${player?.ready ? ' is-ready' : ''}${player?.isHost ? ' is-host' : ''}`}>
                  {player ? (
                    <>
                      <img
                        src={player.avatarId ? getAvatarUrl(player.avatarId) : avatarFallback}
                        alt=""
                        draggable={false}
                        onError={event => { event.currentTarget.src = avatarFallback }}
                      />
                      <div className="majak-mobile-room-seat__info">
                        <strong>{player.name || player.playerId}</strong>
                        <span>{levelText} {ratingText}</span>
                        <em>{player.isHost ? '親方' : player.ready ? '準備完了' : '待機中'}</em>
                      </div>
                    </>
                  ) : (
                    <div className="majak-mobile-room-seat__empty">空席</div>
                  )}
                </article>
              )
            })}
          </div>

          {responsivePromptText && <p className="majak-mobile-room-prompt-message" role="status">{responsivePromptText}</p>}

          <div className="majak-mobile-room-primary-actions">
            <button type="button" onClick={() => { void exitRoomToLobby(me?.pos, isViewerUser) }}>退室</button>
            {hasPlayerSeat && (
              <button
                type="button"
                className={`majak-mobile-room-ready-button${isReady ? ' is-active' : ''}${readyButtonAttention ? ' is-attention' : ''}`}
                onClick={onToggleReady}
                disabled={readyButtonDisabled}
              >
                {readyLabel}
              </button>
            )}
            {hasPlayerSeat && <button type="button" onClick={openInviteList} disabled={autoMatchingChannel}>招待</button>}
            {chanceButtonVisible && hasPlayerSeat && <button type="button" className={chanceReserved ? 'is-active' : undefined} onClick={() => { void onReserveChance() }} disabled={!chanceButtonSendAllowed}>チャンス</button>}
          </div>
        </section>

        <aside className="majak-mobile-room-side">
          <div className="majak-mobile-room-log majak-mobile-room-log--status" ref={statusLogRef}>
            {statusLog.map(message => <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>{message.text}</div>)}
          </div>

          <div className="majak-mobile-room-log majak-mobile-room-log--chat" ref={chatLogRef}>
            {chatLog.map(message => (
              <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>
                {message.name ? `${message.name} : ${message.text}` : message.text}
              </div>
            ))}
          </div>

          <div className="majak-mobile-room-chat-row">
            <input
              ref={chatInputRef}
              value={chatText}
              onChange={event => setChatText(event.target.value)}
              onKeyDown={onChatKeyDown}
              maxLength={80}
              disabled={chatInputDisabled}
            />
            <button type="button" onClick={() => { void sendChat() }} disabled={chatInputDisabled || !chatText.trim()}>送信</button>
          </div>
        </aside>

        {showInviteList && (
          <MiniChannelWnd
            channelId={channelId}
            members={channelMembers}
            fullScreen
            compact
            onClose={() => setShowInviteList(false)}
            onReqGame={pix => void onGameInvi(pix)}
            onViewProfile={openChannelMemberInfo}
          />
        )}

        {selectedViewer && <PlayerInfoWnd player={selectedViewer} onClose={() => setSelectedViewer(null)} />}

        {showCfg && (
          <CfgDlg
            initial={roomCfg}
            onOK={cfg => { saveMajakConfig(cfg); setRoomCfg(cfg); setShowCfg(false) }}
            onCancel={() => setShowCfg(false)}
            onModify={configureMajakSound}
          />
        )}

        {showAccuse && (
          <AccuseDlg
            myPix={myPix}
            myMemberName={displayNameForPix(myPix)}
            speakers={accuseSpeakers}
            speakerNameById={accuseSpeakerNameById}
            chatContent={accuseChatContent}
            onOK={async payload => {
              await sendAccuseComplaint({ pix: myPix, channelId, roomId: Number(roomId), ...payload })
              void showMessage('通報を受け付けました。', 'お知らせ')
            }}
            onClose={() => setShowAccuse(false)}
          />
        )}

        {displayedHanResData && !announceData && pendingAnnounces.length === 0 && (
          <HanRes
            players={displayedHanResData}
            hasTor={hanResFlags.hasTor}
            hasTip={FORCE_HAN_RESULT_FOR_TEST || hanResFlags.hasTip}
            isViewer={hanResFlags.isViewer}
            isTournament={hanResFlags.isTournament}
            displayScale={isMobileIngame ? mobileHanResScale : 1}
            displayOffsetY={isMobileIngame ? MOBILE_HAN_RES_OFFSET_Y : 0}
            backdrop
            onClose={() => {
              if (FORCE_HAN_RESULT_FOR_TEST && !forcedHanResultDismissed) setForcedHanResultDismissed(true)
              else closeHanRes()
            }}
          />
        )}

        <SlideAnnounce data={announceData} onDone={() => setAnnounceData(null)} top={7} />
      </div>
    )
  }

  if (inlineGame) {
    const displayedKyoResData = FORCE_KYO_RESULT_FOR_TEST ? FORCED_KYO_RESULT : kyoResData
    const kyoResultOverlay = displayedKyoResData && !displayedHanResData && !announceData && pendingAnnounces.length === 0 ? (
      <KyoRes
        data={displayedKyoResData}
        myOdr={displayedKyoResData.players.find(player => player.pix === myPix)?.seatPos ?? 0}
        isViewer={isViewerUser}
        canContinue={FORCE_KYO_RESULT_FOR_TEST || Boolean(kyoResultAction)}
        waitingForOtherPlayers={kyoResultSubmitted}
        playerProgress={kyoResultProgress}
        onClose={() => { if (!FORCE_KYO_RESULT_FOR_TEST) void sendKyoResultAction() }}
      />
    ) : null
    const levelUpOverlay = levelUp && !announceData && pendingAnnounces.length === 0 ? (
      <LevelupDlg
        level={levelUp.level}
        lentMoney={levelUp.lentMoney}
        onClose={() => setLevelUp(null)}
      />
    ) : null
    const hanResultOverlay = displayedHanResData && !levelUp && !announceData && pendingAnnounces.length === 0 ? (
      <HanRes
        players={displayedHanResData}
        hasTor={hanResFlags.hasTor}
        hasTip={FORCE_HAN_RESULT_FOR_TEST || hanResFlags.hasTip}
        isViewer={hanResFlags.isViewer}
        isTournament={hanResFlags.isTournament}
        displayScale={isMobileIngame ? mobileHanResScale : 1}
        displayOffsetY={isMobileIngame ? MOBILE_HAN_RES_OFFSET_Y : 0}
        backdrop
        onClose={() => {
          if (FORCE_HAN_RESULT_FOR_TEST && !forcedHanResultDismissed) setForcedHanResultDismissed(true)
          else closeHanRes()
        }}
      />
    ) : null

    const inlineGameStage = (
      <div className="majak-inline-game-stage" style={{ width: ingameLayoutMode === 'responsiveDesktop' ? '100%' : ROOM_W, height: ingameLayoutMode === 'responsiveDesktop' ? '100%' : ROOM_H, overflow: 'hidden', background: isMobileIngame || ingameLayoutMode === 'responsiveDesktop' ? 'transparent' : '#000' }}>
        <div ref={inlineGameRef} style={{ position: 'absolute', left: 0, top: ingameLayoutMode === 'responsiveDesktop' ? 0 : -31, width: ingameLayoutMode === 'responsiveDesktop' ? '100%' : GAME_WIDTH, height: ingameLayoutMode === 'responsiveDesktop' ? '100%' : GAME_HEIGHT }} />

        <div style={{ position: 'absolute', left: 0, top: 0, width: ROOM_W, height: ROOM_H, zIndex: 24, pointerEvents: 'none' }}>
          {activeEmoticons.map(item => <EmoticonAnimation key={item.id} item={item} now={emoticonNow} />)}
        </div>

        {showCfg && (
          <CfgDlg
            initial={roomCfg}
            onOK={cfg => { saveMajakConfig(cfg); setRoomCfg(cfg); setShowCfg(false) }}
            onCancel={() => setShowCfg(false)}
            onModify={configureMajakSound}
          />
        )}

        {showAccuse && (
          <AccuseDlg
            myPix={myPix}
            myMemberName={displayNameForPix(myPix)}
            speakers={accuseSpeakers}
            speakerNameById={accuseSpeakerNameById}
            chatContent={accuseChatContent}
            onOK={async payload => {
              await sendAccuseComplaint({ pix: myPix, channelId, roomId: Number(roomId), ...payload })
              void showMessage('通報を受け付けました。', 'お知らせ')
            }}
            onClose={() => setShowAccuse(false)}
          />
        )}

        <SlideAnnounce
          data={announceData}
          onDone={() => setAnnounceData(null)}
          top={7}
        />
      </div>
    )

    if (isMobileIngame) {
      return (
        <div ref={mobileIngameShellRef} className="majak-mobile-ingame-shell" style={gameSurroundStyle}>
          <div
            className="majak-mobile-ingame-scale"
            style={{
              left: 0,
              width: MOBILE_INGAME_FOCUS_W,
              height: ROOM_H,
              overflow: 'hidden',
              transform: `translate(${mobileIngameOffsetX}px, ${mobileIngameOffsetY}px) scale(${mobileIngameScale})`,
              transformOrigin: 'top left',
            }}
          >
            {inlineGameStage}
          </div>
          {kyoResultOverlay}
          {levelUpOverlay}
          {hanResultOverlay}
          <GameReconnectLoading visible={inlineGameLoading} currentStep={gameLoadStep} complete={gameLoadComplete} />
          {!mobileIngameChatOpen && (
            <div className={`majak-mobile-ingame-tool-drawer${mobileIngameToolOpen ? ' is-open' : ''}`} style={mobileIngameToolDrawerStyle}>
              <button
                type="button"
                className="majak-mobile-ingame-tool-toggle"
                onClick={() => setMobileIngameToolOpen(open => {
                  const nextOpen = !open
                  if (!nextOpen) setMobileIngameChatOpen(false)
                  return nextOpen
                })}
                aria-expanded={mobileIngameToolOpen}
              >
                {mobileIngameToolOpen ? '▲' : '▼'}
              </button>
              <div className="majak-mobile-ingame-action-bar">
                {hasInlineGamePlayerControls && (
                  <>
                    <button type="button" onClick={openInviteList} disabled={autoMatchingChannel}>招待</button>
                    <button type="button" className={autoControl.autoPass ? 'is-active' : undefined} onClick={onSetPass} disabled={!childAutoControlEnabled}>オートパス</button>
                    <button type="button" className={autoControl.autoHora ? 'is-active' : undefined} onClick={onSetHora} disabled={!childAutoControlEnabled}>オート和了</button>
                    <button type="button" className={autoControl.autoTap ? 'is-active' : undefined} onClick={onSetAuto} disabled={!childAutoControlEnabled}>ツモ切り</button>
                    <button type="button" className={autoControl.prox ? 'is-active' : undefined} onClick={onSetProx} disabled={!autoControlEnabled}>代打ち</button>
                    {chanceButtonVisible && <button type="button" className={chanceReserved ? 'is-active' : undefined} onClick={() => { void onReserveChance() }} disabled={!chanceButtonSendAllowed}>チャンス</button>}
                  </>
                )}
                {isViewerUser && (
                  <>
                    <button type="button" onClick={() => onViewerRotate(3)}>回転3</button>
                    <button type="button" onClick={() => onViewerRotate(1)}>回転1</button>
                    <button type="button" disabled>捨て牌</button>
                    <button type="button" onClick={() => { void exitRoomToLobby(undefined, true) }}>退室</button>
                    <button
                      type="button"
                      className={viewerHandHidden ? 'is-active' : undefined}
                      onClick={onViewerHandToggle}
                      disabled={!viewerHandOpenEnabled}
                    >
                      手牌
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="majak-mobile-ingame-chat-toggle"
                  onClick={() => {
                    if (chatInputDisabled) return
                    setMobileIngameChatOpen(true)
                  }}
                  aria-expanded={mobileIngameChatOpen}
                  disabled={chatInputDisabled}
                >
                  チャット
                </button>
              </div>
            </div>
          )}
          {mobileIngameChatOpen && (
            <aside className={`majak-mobile-ingame-chat-panel${tengokuBoardSkin ? ' is-tengoku-skin' : ''}`}>
              <button
                type="button"
                className="majak-mobile-ingame-chat-toggle majak-mobile-ingame-chat-panel__close is-open"
                onClick={() => setMobileIngameChatOpen(false)}
                aria-expanded={mobileIngameChatOpen}
              >
                閉じる
              </button>
              <div className="majak-mobile-ingame-chat-panel__status" ref={statusLogRef}>
                {statusLog.map(message => <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>{message.text}</div>)}
              </div>
              <div className="majak-mobile-ingame-chat-panel__log" ref={chatLogRef}>
                {chatLog.map(message => (
                  <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>
                    {message.name ? `${message.name} : ${message.text}` : message.text}
                  </div>
                ))}
              </div>
              <div className="majak-mobile-ingame-chat-panel__input">
                <input
                  ref={chatInputRef}
                  value={chatText}
                  onChange={event => setChatText(event.target.value)}
                  onKeyDown={onChatKeyDown}
                  maxLength={80}
                  disabled={chatInputDisabled}
                />
                <button type="button" onClick={() => { void sendChat() }} disabled={chatInputDisabled || !chatText.trim()}>送信</button>
              </div>
            </aside>
          )}
          {showInviteList && (
            <MiniChannelWnd
              channelId={channelId}
              members={channelMembers}
              fullScreen
              compact
              placement="bottom"
              onClose={() => setShowInviteList(false)}
              onReqGame={pix => void onGameInvi(pix)}
              onViewProfile={openChannelMemberInfo}
            />
          )}
        </div>
      )
    }

    return (
      <div className={`majak-responsive-ingame-shell${inlineGameLoading ? ' is-loading' : ''}`} style={gameSurroundStyle}>
        <div className="majak-responsive-ingame-playfield" style={gameSurroundStyle}>
          <div className="majak-responsive-ingame-world">
            {inlineGameStage}
          </div>
          <div className="majak-responsive-ingame-loading">
            <GameReconnectLoading visible={inlineGameLoading} currentStep={gameLoadStep} complete={gameLoadComplete} />
          </div>
        </div>
        {!inlineGameLoading && (
          <aside className={`majak-responsive-ingame-sidebar${tengokuBoardSkin ? ' is-tengoku-skin' : ''}`}>
            <div className="majak-responsive-ingame-sidebar__status" ref={statusLogRef}>
              {isViewerUser && statusLog.length === 0
                ? <img className="majak-responsive-ingame-sidebar__brand" src="/assets/images/common/ico_big_majak4.jpg" alt="麻雀4" draggable={false} />
                : statusLog.map(message => <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>{message.text}</div>)}
            </div>
            <section className="majak-responsive-ingame-sidebar__viewers" aria-label="観戦者一覧">
              <header>
                <span>観戦者</span>
                <strong>{viewers.length}</strong>
              </header>
              <div className="majak-responsive-ingame-sidebar__viewer-list majak-mobile-lobby-members">
                {viewers.length === 0
                  ? <span className="majak-responsive-ingame-sidebar__viewer-empty">観戦者はいません</span>
                  : viewers.map(viewer => {
                    const fallback = getDefaultAvatarUrl(viewer.sex === 'F' || viewer.sex === 'female' ? 'female' : 'male')
                    return (
                      <div key={viewer.pix} className={`majak-mobile-lobby-member${selectedViewer?.pix === viewer.pix ? ' is-selected' : ''}`}>
                        <div className="majak-mobile-lobby-member__row">
                          <button type="button" className="majak-mobile-lobby-member__select" title={viewer.name || viewer.pix} onClick={() => setSelectedViewer({
                            pix: viewer.pix,
                            name: viewer.name,
                            avatarId: viewer.avatarId,
                            sex: viewer.sex === 'F' || viewer.sex === 'female' ? 'female' : 'male',
                            rating: viewer.rating,
                            slevel: viewer.slevel ?? viewer.dan,
                          })}>
                            <img
                              src={viewer.avatarId ? getShortAvatarUrl(viewer.avatarId) : fallback}
                              alt=""
                              onError={event => { handleShortAvatarError(event.currentTarget, viewer.avatarId, viewer.sex === 'F' || viewer.sex === 'female' ? 'female' : 'male') }}
                            />
                            <span className="majak-mobile-lobby-member__identity">
                              <span className="majak-mobile-lobby-member__name">{viewer.name || viewer.pix}</span>
                              <span className="majak-mobile-lobby-member__title">{viewer.slevel || viewer.dan || '庶民'}</span>
                              <span className="majak-mobile-lobby-member__location">観戦中</span>
                            </span>
                          </button>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </section>
            <div className="majak-responsive-ingame-sidebar__chat" ref={chatLogRef}>
              {chatLog.map(message => (
                <div key={message.id} style={{ color: message.color ?? undefined, fontWeight: message.bold ? 'bold' : undefined }}>
                  {message.name ? `${message.name} : ${message.text}` : message.text}
                </div>
              ))}
            </div>
            <div className="majak-responsive-ingame-sidebar__input">
              <input ref={chatInputRef} value={chatText} onChange={event => setChatText(event.target.value)} onKeyDown={onChatKeyDown} maxLength={80} disabled={chatInputDisabled} />
              <button type="button" onClick={() => { void sendChat() }} disabled={chatInputDisabled || !chatText.trim()}>送信</button>
            </div>
            <div className={`majak-responsive-ingame-sidebar__actions${isViewerUser ? ' is-viewer' : ''}`}>
              {hasInlineGamePlayerControls && (
                <>
                  <button type="button" onClick={openInviteList} disabled={autoMatchingChannel}>招待</button>
                  <button type="button" className={autoControl.autoPass ? 'is-active' : undefined} onClick={onSetPass} disabled={!childAutoControlEnabled}>オートパス</button>
                  <button type="button" className={autoControl.autoHora ? 'is-active' : undefined} onClick={onSetHora} disabled={!childAutoControlEnabled}>オート和了</button>
                  <button type="button" className={autoControl.autoTap ? 'is-active' : undefined} onClick={onSetAuto} disabled={!childAutoControlEnabled}>ツモ切り</button>
                  <button type="button" className={autoControl.prox ? 'is-active' : undefined} onClick={onSetProx} disabled={!autoControlEnabled}>代打ち</button>
                </>
              )}
              {isViewerUser && (
                <>
                  <button type="button" onClick={() => onViewerRotate(3)}>左回転</button>
                  <button type="button" onClick={() => onViewerRotate(1)}>右回転</button>
                  <button type="button" className={viewerHandHidden ? 'is-active' : undefined} onClick={onViewerHandToggle} disabled={!viewerHandOpenEnabled}>手牌</button>
                  <button type="button" onClick={() => { void exitRoomToLobby(undefined, true) }}>退室</button>
                </>
              )}
            </div>
          </aside>
        )}
        {selectedViewer && <PlayerInfoWnd player={selectedViewer} onClose={() => setSelectedViewer(null)} />}
        {kyoResultOverlay}
        {levelUpOverlay}
        {hanResultOverlay}
        {showInviteList && (
          <MiniChannelWnd
            channelId={channelId}
            members={channelMembers}
            fullScreen
            onClose={() => setShowInviteList(false)}
            onReqGame={pix => void onGameInvi(pix)}
            onViewProfile={openChannelMemberInfo}
          />
        )}
      </div>
    )
  }

  const waitingRoomStage = (
    /* CMJRoomWnd content area below MajakFrame title bar: W_BOARD/H_BOARD = 789×704 */
    <div style={{ position: 'relative', width: ROOM_W, height: ROOM_H, overflow: 'hidden' }}>
      <style>{`
        @keyframes majakRoomPromptFade {
          0% { opacity: 0.5; }
          50% { opacity: 1; }
          100% { opacity: 0.5; }
        }
      `}</style>

      {/* ── ゲーム卓エリア: m_wndGame.Create(5, 31, this) → content y=0 ── */}
      <img
        src={customBoardSrc}
        alt=""
        draggable={false}
                        onError={event => { event.currentTarget.src = getLegacyBoardImageUrl(undefined) }}
        style={{ position: 'absolute', left: BOARD_X, top: BOARD_Y, width: 789, height: 704 }}
      />

      {/* ── CMJGameWnd::PutPanel: PANELMODE_ROOM=mj_resBtBoard, PANELMODE_VIEW=mj_watchBoard at X_PANEL=102, Y_PANEL=644 */}
      <img
        src={bgSkinSrc(isViewerUser ? 'mj_watchBoard' : 'mj_resBtBoard')}
        alt=""
        draggable={false}
        onError={event => { event.currentTarget.src = `${IMG}/${isViewerUser ? 'mj_watchBoard' : 'mj_resBtBoard'}.png` }}
        style={{ position: 'absolute', left: 102, top: 644, width: 580, height: 60, pointerEvents: 'none' }}
      />

      {/* ── CMJTblUser::Prompt: Set(209-5,315-31) on m_wndGame → content (204,284), z=1000, 2s alpha fade ── */}
      {promptImage && (
        <img
          src={promptImage}
          alt=""
          draggable={false}
          onError={event => { event.currentTarget.src = defaultSkinSrc(event.currentTarget.src) }}
          style={{
            position: 'absolute',
            left: 204,
            top: 284,
            width: 379,
            height: 139,
            zIndex: 1000,
            animation: 'majakRoomPromptFade 2s linear infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* ── CMJTblDraw::PutOdrBox / PutWaitMark — mempos[] coordinates on m_wndGame ── */}
      {players.map(player => {
        const pos = MEM_POS[seatToLegacyLoc(player.pos, me?.pos)]
        const avatarPos = roomPoint(pos.avt)
        const namePos = roomPoint(pos.name)
        const textPos = roomPoint(pos.text)
        const waitPos = roomPoint(pos.gls)
        const titlePos = roomPoint(pos.ttl)
        const trickPos = roomPoint(pos.trk)
        const avatarFallback = getDefaultAvatarUrl(player.sex === 'F' || player.sex === 'female' ? 'female' : 'male')
        const majakTitleSrc = getMajakTitleSrc(player.majakTitle)
        const trickTitleSrc = getTrickTitleSrc(player.trickTitle)
        const isProxyPlayer = Boolean(player.isProxy) || (player.playerId === myPix && autoControl.prox)
        const playerTextColor = isProxyPlayer ? 'rgb(224,0,0)' : '#fff'
        const levelText = skillChannel ? `@ ${Number(player.skillCnt ?? 0)}局` : player.slevel || ''
        const ratingText = !skillChannel && player.rating > 0 && Number(player.nlevel ?? 0) < 100
          ? `[${String(player.rating).padStart(4, ' ')}]`
          : ''
        const seatPanelLeft = namePos.left
        const seatPanelRight = namePos.left + 98
        const avatarRight = avatarPos.left + 45
        const textBlockLeft = avatarPos.left < namePos.left + 49 ? avatarRight : seatPanelLeft
        const textBlockWidth = avatarPos.left < namePos.left + 49 ? seatPanelRight - avatarRight : avatarPos.left - seatPanelLeft
        return (
          <div
            key={player.playerId}
            style={{ position: 'absolute', left: 0, top: 0, width: ROOM_W, height: ROOM_H, zIndex: 10, pointerEvents: 'none' }}
          >
            <img
              src={player.avatarId ? getAvatarUrl(player.avatarId) : avatarFallback}
              alt=""
              draggable={false}
              onError={e => {
                e.currentTarget.src = avatarFallback
              }}
              style={{
                position: 'absolute',
                left: avatarPos.left,
                top: avatarPos.top,
                width: 45,
                height: 102,
                objectFit: 'contain',
                zIndex: 10,
                pointerEvents: 'none',
              }}
            />
            {majakTitleSrc && (
              <img
                src={majakTitleSrc}
                alt=""
                draggable={false}
                style={{ position: 'absolute', left: titlePos.left, top: titlePos.top, zIndex: 12, pointerEvents: 'none' }}
              />
            )}
            {trickTitleSrc && (
              <img
                src={trickTitleSrc}
                alt=""
                draggable={false}
                style={{ position: 'absolute', left: trickPos.left, top: trickPos.top, zIndex: 11, pointerEvents: 'none' }}
              />
            )}
            <div style={{
              position: 'absolute',
              left: namePos.left,
              top: namePos.top,
              width: 98,
              height: 14,
              fontFamily: 'var(--majak-font-family-ui)',
              fontSize: 'calc(12px * var(--majak-type-scale))',
              lineHeight: '14px',
              color: playerTextColor,
              zIndex: 10,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              pointerEvents: 'none',
            }}>
              {player.name}
            </div>
            <div style={{
              position: 'absolute',
              left: textBlockLeft,
              top: textPos.top,
              width: textBlockWidth,
              height: 30,
              fontFamily: 'var(--majak-font-family-ui)',
              fontSize: 'calc(12px * var(--majak-type-scale))',
              lineHeight: '15px',
              color: playerTextColor,
              zIndex: 10,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              pointerEvents: 'none',
            }}>
              <div>{levelText}</div>
              <div>{ratingText}</div>
            </div>
            {!autoMatchingChannel && !player.ready && (
              <img
                src={`${IMG}/mj_IcnWait.png`}
                alt=""
                draggable={false}
                style={{ position: 'absolute', left: waitPos.left, top: waitPos.top, width: 20, height: 22, zIndex: 11, pointerEvents: 'none' }}
              />
            )}
          </div>
        )
      })}

      {/* ── サイドバー背景 mj_sideBg.png (225×704) X_SIDEBAR=794, Y_SIDEBAR=31 → content y=0 ── */}
      <img
        src={bgSkinSrc('mj_sideBg')}
        alt=""
        draggable={false}
        onError={event => { event.currentTarget.src = `${IMG}/mj_sideBg.png` }}
        style={{ position: 'absolute', left: 794, top: 0, width: 225, height: 704 }}
      />

      {/* ── ルームタイトル MAJAK_ROOMTITLE_LEFT=815 TOP=49 W=126 H=14 ── */}
      <div
        style={{
          position: 'absolute',
          left: 815,
          top: 18,
          width: 126,
          height: 14,
          fontFamily: 'var(--majak-font-family-ui)',
          fontSize: 'calc(12px * var(--majak-type-scale))',
          lineHeight: '14px',
          color: legacyPalette.roomTitle,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {roomTitle}
      </div>

      {/* ── ルールアイコン: MJRoomWnd1.cpp OnPaint BitBlt(948+17*n,39/56) → content y=8/25 ── */}
      {optionIcons}

      {/* ── ステータスログ HG_GAME_STATUSWND: (808,81)-(1016,211) ──
           プレイヤー情報 (PlayerList 受信後) を上部に表示し、
           ステータスメッセージをその下に表示する。
           CMJTblUser::PutScore / 座席情報 相当 */}
      <div
        ref={statusLogRef}
        className="majak-room-scroll"
        style={{
          position: 'absolute',
          left: 808,
          top: 50,
          width: 208,   /* 1016-808 */
          height: 130,  /* 211-81 */
          boxSizing: 'border-box',
          paddingLeft: 5,
          paddingRight: 9,
          overflowY: 'auto',
          overflowX: 'hidden',
          fontFamily: 'var(--majak-font-family-ui)',
          fontSize: 'calc(11px * var(--majak-type-scale))',
          color: '#000',
          background: 'transparent',
          pointerEvents: 'none',
          display: announceData ? 'none' : undefined,
        }}
      >
        {/* ステータスメッセージ */}
        {statusLog.map(m => (
          <div key={m.id} style={{ color: m.color ?? '#000', fontWeight: m.bold ? 'bold' : undefined }}>{m.text}</div>
        ))}
      </div>

      {/* ── 観戦者アバター領域: frame Y_VIEWER_WINDOW=235 → content y=204 ── */}
      <ViewerListWnd viewers={viewers} y={204} />

      {selectedViewer && (
        <PlayerInfoWnd
          player={selectedViewer}
          onClose={() => setSelectedViewer(null)}
        />
      )}

      {/* ── お知らせ HG_GAME_NOTICE: (809,325)-(1009,337) ── */}
      <div
        style={{
          position: 'absolute',
          left: 809,
          top: 294,
          width: 200,   /* 1009-809 */
          height: 12,   /* 337-325 */
          fontFamily: 'var(--majak-font-family-ui)',
          fontSize: 'calc(10px * var(--majak-type-scale))',
          color: notice?.color ?? 'rgb(254,225,225)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 25,
          display: announceData ? 'none' : undefined,
          textShadow: '1px 1px 0 rgba(0,0,0,0.65)',
        }}
      >
        {notice?.text ?? ''}
      </div>

      {/* ── チャットログ HG_GAME_CHATLIST: (808,345)-(1016,603) ── */}
      <div
        ref={chatLogRef}
        className="majak-room-scroll"
        style={{
          position: 'absolute',
          left: 808,
          top: 314,
          width: 208,   /* 1016-808 */
          height: 258,  /* 603-345 */
          boxSizing: 'border-box',
          paddingLeft: 5,
          paddingRight: 9,
          overflowY: 'auto',
          overflowX: 'hidden',
          fontFamily: 'var(--majak-font-family-ui)',
          fontSize: 'calc(11px * var(--majak-type-scale))',
          color: '#000',
          background: 'transparent',
        }}
      >
        {chatLog.map(m => (
          /* レガシー MJRoomWnd1.cpp:832 strHeader = pszPix + " : ";
             « 名前と本文を 1色 で描画 (名前のみ別色 はレガシーに存在しない) */
          <div key={m.id} style={{ color: m.color ?? '#000', fontWeight: m.bold ? 'bold' : undefined }}>
            {m.name ? `${m.name} : ${m.text}` : m.text}
          </div>
        ))}
      </div>

      {/* ── チャット入力 HG_GAME_CHATEDIT: (809,624)-(1010,640) ── */}
      <input
        ref={chatInputRef}
        value={chatText}
        onChange={e => setChatText(e.target.value)}
        onKeyDown={onChatKeyDown}
        maxLength={80}
        disabled={chatInputDisabled}
        style={{
          position: 'absolute',
          left: 809,
          top: 593,
          width: 201,   /* 1010-809 */
          height: 16,   /* 640-624 */
          fontFamily: 'var(--majak-font-family-ui)',
          fontSize: 'calc(11px * var(--majak-type-scale))',
          background: legacyPalette.chatEditBack,
          color: legacyPalette.chatEditText,
          border: 'none',
          outline: 'none',
          padding: '0 2px',
          opacity: chatInputDisabled ? 0.65 : 1,
        }}
      />

      {/* ── 観戦 PANELMODE_VIEW: X_VIEWROT3/1, X_VIEWHIDE, X_KYOHIDE, X_KYOCONT */}
      <SpriteButton
        src={bgSkinSrc('mj_btPaifuRot3')}
        frameW={46} frameH={31}
        x={118} y={647}
        onClick={() => onViewerRotate(3)}
        title="回転3"
        hidden={!isViewerUser}
      />
      <SpriteButton
        src={bgSkinSrc('mj_btPaifuRot1')}
        frameW={46} frameH={31}
        x={164} y={647}
        onClick={() => onViewerRotate(1)}
        title="回転1"
        hidden={!isViewerUser}
      />
      <SpriteButton
        src={bgSkinSrc('mj_btLookSutehai')}
        frameW={116} frameH={40}
        x={435} y={647}
        onClick={() => {}}
        title="捨て牌表示"
        hidden={!isViewerUser}
        disabled
      />

      {/* ── 退室ボタン: X_SETEXIT=440-5, Y_SETEXIT=678-31 on m_wndGame → content (435,647) */}
      <SpriteButton
        src={bgSkinSrc('mj_btExitGame')}
        frameW={116} frameH={40}
        x={isViewerUser ? 558 : 435} y={647}
        onClick={() => { void exitRoomToLobby(me?.pos, isViewerUser) }}
        title="退室"
      />
      <SpriteButton
        src={bgSkinSrc('mj_btPaifuHide')}
        frameW={92} frameH={25}
        x={118} y={676}
        onClick={onViewerHandToggle}
        title="手牌表示切替"
        hidden={!isViewerUser}
        disabled={!viewerHandOpenEnabled}
        checked={viewerHandHidden}
      />

       {/* ── OK (準備完了) ボタン: X_KYOCONT=563-5, Y_KYOCONT=678-31 on m_wndGame → content (558,647)
           GetSetContBtn 相当: OnToggleReady → SendRdyPlay
         レガシー PANELMODE_ROOM 初回(nPrevMode==PANELMODE_NONE) は m_btnKyoCont(mj_btOk) を表示 */}
      <SpriteButton
        src={readyButtonImage}
        fallbackSrc={readyButtonFallbackImage}
        frameW={116} frameH={40}
        x={558} y={647}
        onClick={onToggleReady}
        title={autoMatchingChannel ? undefined : trainingChannel && me?.isHost ? '開始' : isReady ? '準備完了 (キャンセル)' : 'OK (準備完了)'}
        hidden={!hasPlayerSeat}
        disabled={readyButtonDisabled}
        checked={isReady}
      />

      {/* ── 右下操作ボタン: m_pRoomWnd->GetScreen() はサイドバー用。full x=X_SET*, content y=Y_SET*-31 */}
      <SpriteButton
        src={bgSkinSrc('mj_btAutoPass')}
        frameW={71} frameH={28}
        x={802} y={646}
        onClick={onSetPass}
        title="オートパス"
        hidden={!hasPlayerSeat}
        disabled={!childAutoControlEnabled}
        checked={autoControl.autoPass}
      />
      <SpriteButton
        src={bgSkinSrc('mj_btAutoHoura')}
        frameW={71} frameH={28}
        x={874} y={646}
        onClick={onSetHora}
        title="オート和了"
        hidden={!hasPlayerSeat}
        disabled={!childAutoControlEnabled}
        checked={autoControl.autoHora}
      />
      <SpriteButton
        src={bgSkinSrc('mj_btDaiuchi')}
        frameW={71} frameH={56}
        x={946} y={646}
        onClick={onSetProx}
        title="代打ち"
        hidden={!hasPlayerSeat}
        disabled={!autoControlEnabled}
        checked={autoControl.prox}
      />
      <SpriteButton
        src={bgSkinSrc('mj_btTsumoGiri')}
        frameW={71} frameH={28}
        x={802} y={674}
        onClick={onSetAuto}
        title="ツモ切り"
        hidden={!hasPlayerSeat}
        disabled={!childAutoControlEnabled}
        checked={autoControl.autoTap}
      />

      {/* ── 招待ボタン mj_btInvite.png 80×23 at (X_ROOMINVI-X_SIDEBAR+X_SIDEBAR, Y_ROOMINVI-Y_SIDEBAR+Y_SIDEBAR)
           = full-window (803,649) → MajakFrame content (803,618)
           原典: m_btnInvi.Create("mj_btInvite", ..., X_ROOMINVI-X_SIDEBAR, Y_ROOMINVI-Y_SIDEBAR, ...)
           クリック時はメンバーリストを表示し、選択した pix を招待する。 */}
      <SpriteButton
        src={bgSkinSrc('mj_btInvite')}
        frameW={106} frameH={26}
        x={803} y={618}
        onClick={openInviteList}
        title="招待"
        hidden={!hasPlayerSeat}
        disabled={autoMatchingChannel}
      />
      <SpriteButton
        src={`${IMG}/mj_btChance.png`}
        frameW={40} frameH={20}
        x={628} y={569}
        onClick={() => { void onReserveChance() }}
        title="チャンス"
        hidden={!chanceButtonVisible || !hasPlayerSeat}
        checked={chanceReserved}
      />

      {showInviteList && !isMobileIngame && (
        <MiniChannelWnd
          channelId={channelId}
          members={channelMembers}
          fullScreen
          onClose={() => setShowInviteList(false)}
          onReqGame={pix => void onGameInvi(pix)}
          onViewProfile={openChannelMemberInfo}
        />
      )}

      {/* ── CMJCfgDlg: ゲーム設定 (設定ボタン押下時) ── */}
      {showCfg && (
        <CfgDlg
          initial={roomCfg}
          onOK={cfg => { saveMajakConfig(cfg); setRoomCfg(cfg); setShowCfg(false) }}
          onCancel={() => setShowCfg(false)}
          onModify={configureMajakSound}
        />
      )}

      {showAccuse && (
        <AccuseDlg
          myPix={myPix}
          myMemberName={displayNameForPix(myPix)}
          speakers={accuseSpeakers}
          speakerNameById={accuseSpeakerNameById}
          chatContent={accuseChatContent}
          onOK={async payload => {
            await sendAccuseComplaint({ pix: myPix, channelId, roomId: Number(roomId), ...payload })
            void showMessage('通報を受け付けました。', 'お知らせ')
          }}
          onClose={() => setShowAccuse(false)}
        />
      )}

      {displayedHanResData && !announceData && pendingAnnounces.length === 0 && (
        <HanRes
          players={displayedHanResData}
          hasTor={hanResFlags.hasTor}
          hasTip={FORCE_HAN_RESULT_FOR_TEST || hanResFlags.hasTip}
          isViewer={hanResFlags.isViewer}
          isTournament={hanResFlags.isTournament}
          displayScale={isMobileIngame ? mobileHanResScale : 1}
          displayOffsetY={isMobileIngame ? MOBILE_HAN_RES_OFFSET_Y : 0}
          backdrop
          onClose={() => {
            if (FORCE_HAN_RESULT_FOR_TEST && !forcedHanResultDismissed) setForcedHanResultDismissed(true)
            else closeHanRes()
          }}
        />
      )}

      {/* ── CMJSlideAnnounce: mjkc19e/mjkc22e 受信時のスライド公告 ── */}
      <SlideAnnounce
        data={announceData}
        onDone={() => setAnnounceData(null)}
        top={7}
      />

    </div>
  )

  if (isMobileIngame) {
    return (
      <div ref={mobileIngameShellRef} className="majak-mobile-ingame-shell majak-mobile-room-waiting-shell">
        <div
          className="majak-mobile-ingame-scale"
          style={{
            width: ROOM_W,
            height: ROOM_H,
            transform: `translateX(-50%) translateY(${mobileIngameOffsetY}px) scale(${mobileIngameScale})`,
          }}
        >
          {waitingRoomStage}
        </div>
        {showInviteList && (
          <MiniChannelWnd
            channelId={channelId}
            members={channelMembers}
            fullScreen
            onClose={() => setShowInviteList(false)}
            onReqGame={pix => void onGameInvi(pix)}
            onViewProfile={openChannelMemberInfo}
          />
        )}
      </div>
    )
  }

  return waitingRoomStage
}


